/**
 * The editor's property panel — `renderProps`, `propsLayer`, `propsCanvas`, `propsFields`,
 * `propsTemplate` and their four `bind*Props` functions (`m08-admin.js:300-625`).
 *
 * Upstream this is one `innerHTML` string per tab plus a binder that wires the markup it just
 * wrote. Here it is one presentational component: it reads the editor's state, the template and
 * the working layout, and hands every change back through callbacks. It knows nothing about the
 * screen that owns them — `EditorScreen` imports this file, never the other way round — and holds
 * no state of its own beyond what an input is holding under the caret.
 *
 * The panel fills `#props`; the `<aside class="card props">` around it is the screen's
 * (`screens.html:148`), exactly as upstream's `renderProps` replaces that element's contents.
 *
 * Three mechanics of the reference are reproduced deliberately, because losing any of them shows:
 *
 *   - `ED.propsKey` (`m08-admin.js:302`) is upstream's answer to «has the panel's STRUCTURE
 *     changed, or only its values». When the key is unchanged the panel is NOT rebuilt; only
 *     `syncPropsFromLayer` pushes x/y/width/rotation back into the inputs. `#propBody` carries
 *     that key here, so a structural change remounts the body the way `innerHTML` does and a
 *     pointer move over the stage does not — the panel would otherwise remount on every drag frame
 *     and take the focus with it. `propsStructureKey` is exported because the screen computes the
 *     same key for its own state.
 *   - `syncPropsFromLayer` (`m08-admin.js:627`) refuses to write into the input that has focus.
 *     `SyncedInput` is that rule: the value comes from the model, except while the field is
 *     focused, when what was typed stands. The screen's drag-time writes go through the same
 *     `[data-l]` attributes, which is why every geometry input still carries one.
 *   - `CommitInput` is the other half — the inputs upstream binds to the DOM `change` event
 *     (canvas size, background placement, validity), which commit on blur or Enter rather than on
 *     every keystroke.
 *
 * A `private` field is never offered to a render layer (`canRender`, in «Katman» and in the
 * «Alanlar» warning alike): that is the visibility contract of §11, not a convenience filter.
 */
import {
  type InputHTMLAttributes,
  memo,
  type ReactElement,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useV07Commands } from '../../data/commands-context';
import { useDemoData } from '../../data/store';
import type { DemoCanvas, DemoFieldDefinition, DemoTemplate } from '../../data/types';
import {
  alphaNote,
  type LibraryAsset,
  type LibraryFont,
  seedAssets,
  seedFonts,
} from '../../lib/asset-library';
import {
  asRenderLayout,
  catalogField,
  defaultTestData,
  edField,
  FIELD_CATALOG,
  FIT_MODES,
  holderFieldKey,
  layerById,
  makeField,
  makeLayer,
  makeSafeArea,
  qrLayer,
  setLocaleBackground,
  textLayer,
  uid,
} from '../../lib/editor-layout';
import {
  analyzeAsset,
  detectSafeBand,
  type FitCheckLayer,
  type FitCheckLayout,
  layerFromBand,
  TEST_NAMES,
  TEST_ORGS,
  verifyLayerPlacement,
} from '../../lib/image-analysis';
import { linkedInIssuerFieldErrors } from '../../lib/linkedin-issuing-organization';
import {
  localeLabel,
  otherLocale,
  publicNameGaps,
  sealedLanguagesOf,
  sealedLocaleGaps,
} from '../../lib/sealed-locales';
import {
  PATTERN_PRESETS,
  type SchemaError,
  validateDefaultValue,
  validateValidationConfig,
} from '../../lib/publish-gate';
import {
  currentRelease,
  dataTypeRec,
  FIELD_DATA_TYPES,
  fieldLabel,
  fmtDateTime,
  PUBLICATION_POLICIES,
  TEMPLATE_STATUS_TR,
  VISIBILITY_TR,
} from '../../lib/reference';
import { type Canvas, canvasH, canvasW, type Layer, minPx, sizePx } from '../../lib/render';
import { CREDENTIAL_TYPES, prototypeNowUTC, SUBJECT_TYPES, tplCount } from '../../lib/template-list';
import {
  assetRefOf,
  CANVAS_PRESETS,
  canRender,
  isGraphicDataType,
  isImageDataType,
  isSystemPrivate,
  slugify,
  VISIBILITY,
} from '../../lib/wizard';
import { useToast } from '../../ui/toast';
import {
  clamp,
  EDITOR_TABS,
  type EditorLayer,
  type EditorLayout,
  type EditorState,
  type EditorTab,
} from './contract';

// ------------------------------------------------------- vocabulary the shared modules keep private

/**
 * `VALIDATION_PROPS_BY_TYPE` — `m01e-schema.js:118`.
 *
 * `lib/publish-gate.ts` ports the same table but keeps it module-private, and that file belongs to
 * another hand this phase, so the rule editor carries its own copy rather than widening its API.
 * The table decides which rule inputs a data type may show, and a rule the type cannot use is a
 * schema error — offering one would print an error the operator has no way to clear.
 */
const VALIDATION_PROPS_BY_TYPE: Record<string, string[]> = {
  string: ['minLength', 'maxLength', 'patternPreset', 'patternMessage'],
  text: ['minLength', 'maxLength', 'patternPreset', 'patternMessage'],
  email: ['maxLength'],
  url: ['maxLength', 'urlPolicy'],
  number: ['min', 'max'],
  integer: ['min', 'max'],
  date: ['dateMin', 'dateMax'],
  datetime: ['dateMin', 'dateMax'],
  boolean: [],
  enum: ['enum', 'minLength', 'maxLength'],
  credential_id: [],
  credential_url: ['urlPolicy'],
  qr: [],
};

/** `URL_POLICY_TR` — `m01e-schema.js:34`, the readable names the select offers. */
const URL_POLICY_TR: Record<string, string> = {
  external: 'Dış bağlantı (http / https)',
  public_credential: 'Public credential adresi (yalnız https)',
  issuer: 'Kurum adresi (yalnız https)',
  linkedin_page: 'LinkedIn kurum sayfası (/company/…)',
  dev: 'Geliştirme (loopback izinli — yayında kullanılmaz)',
};

/** `URL_POLICY_KEYS` — `m01e-schema.js:90`, in `URL_POLICIES`' own declaration order. */
const URL_POLICY_KEYS = ['external', 'public_credential', 'issuer', 'linkedin_page', 'dev'];

/** `PATTERN_PRESET_KEYS` — `m01e-schema.js:32`; the preset table is exported, its key list is not. */
const PATTERN_PRESET_KEYS = Object.keys(PATTERN_PRESETS);

/**
 * `makeCanvas` — `m02-mockdb.js:121`.
 *
 * `lib/wizard.ts` has a copy that always mints `outputScale: 1` and `unit: 'px'`, which is right
 * for a new template and wrong here: «Çıktı ölçeği» cycles 1×→2×→3× through this factory, and a
 * preset or orientation change has to carry the scale the operator already chose.
 */
function makeCanvas(o: { width?: number; height?: number; unit?: string; outputScale?: number }): DemoCanvas {
  const width = Math.max(16, Math.round(o.width || 1200));
  const height = Math.max(16, Math.round(o.height || 1200));
  return {
    width,
    height,
    aspectRatio: Number((width / height).toFixed(6)),
    orientation: width === height ? 'square' : width > height ? 'landscape' : 'portrait',
    unit: o.unit || 'px',
    outputScale: o.outputScale || 1,
  };
}

/** `boundToRender` — `m01-utils.js:165`. A QR or image layer binds a field just as text does. */
const boundToRender = (lay: EditorLayout, key: string): boolean =>
  (lay.layers || []).some(
    (l) => l.fieldKey === key && (l.kind === 'text' || l.kind === 'image' || l.kind === 'qr'),
  );

/** `deepClone` — `m01-utils.js:40`. The JSON round-trip drops `undefined`, as the reference's does. */
const deepClone = <T,>(o: T): T => JSON.parse(JSON.stringify(o)) as T;

type RefResolution = { ok: true; asset: LibraryAsset } | { ok: false; reason: string };

/**
 * `AssetLibrary.resolveRef` — `m01b-assets.js`, fail-closed on the digest.
 *
 * `lib/render/library.ts` has the same resolver over the SEEDED library only; this one runs over
 * the list the panel was given, so a background ingested in this sitting resolves here the way it
 * does upstream. The refusals are the reference's, and a mismatched digest never falls back to the
 * stale asset.
 */
function resolveRef(
  ref: { assetId?: string | undefined; sha256?: string | undefined } | null | undefined,
  assets: LibraryAsset[],
): RefResolution {
  if (!ref?.assetId) return { ok: false, reason: 'missing_ref' };
  const a = assets.find((x) => x.id === ref.assetId);
  if (!a) return { ok: false, reason: 'not_found' };
  if (ref.sha256 && a.sha256 !== ref.sha256) return { ok: false, reason: 'digest_mismatch' };
  return { ok: true, asset: a };
}

/** `fontFamilyList` / `fontWeightList` — `m01-utils.js:162`, over the library the panel was given. */
const familyList = (fonts: LibraryFont[]): string[] => {
  const fams = [...new Set(fonts.map((f) => f.family))];
  return fams.length ? fams : ['Poppins'];
};

const weightList = (fonts: LibraryFont[], family: string | undefined): number[] => {
  const w = [...new Set(fonts.filter((f) => f.family === family).map((f) => f.weight))].sort((a, b) => a - b);
  return w.length ? w : [400, 500, 600, 700];
};

/* The editor's layer/canvas types and the engine's are the same objects under two spellings; the
   crossing happens here rather than at every call site, as `asRenderLayout` does for a layout. */
const asLayer = (l: EditorLayer): Layer => l as unknown as Layer;
const asCanvas = (c: DemoCanvas): Canvas => c as unknown as Canvas;

/** `layerName` — `m08-admin.js:316`. A bound text layer is named by its field's Turkish label. */
function layerName(lay: EditorLayout, x: EditorLayer): string {
  if (x.kind === 'qr') return 'QR';
  if (x.kind === 'image') return 'Görsel';
  if (x.kind === 'static') return `Sabit: ${x.text || '—'}`;
  const f = edField(lay, x.fieldKey);
  return (f && fieldLabel(f, 'tr')) || x.fieldKey || 'Metin';
}

/** `ICON.check` — `m01-utils.js:314`. */
const IconCheck = (): ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

/** `ICON.al` / `ICON.ac` / `ICON.ar` — `m01-utils.js:317-319`. */
const ALIGN_ICON: Record<string, string> = {
  left: 'M4 6h16M4 12h10M4 18h14',
  center: 'M4 6h16M7 12h10M5 18h14',
  right: 'M4 6h16M10 12h10M6 18h14',
};

const ALIGN_LABEL: Record<string, string> = {
  left: 'Sola hizala',
  center: 'Ortala',
  right: 'Sağa hizala',
};

/** `pillTemplate` — `m08-admin.js:8`. */
function TemplatePill({ template }: { template: DemoTemplate }): ReactElement {
  const r = currentRelease(template);
  const cls =
    template.status === 'published'
      ? 'pill-active'
      : template.status === 'draft'
        ? 'pill-info'
        : 'pill-neutral';
  return (
    <span className={`pill ${cls} none`}>
      {`${TEMPLATE_STATUS_TR[template.status] ?? ''}${template.status === 'published' && r ? ` · v${r.releaseNumber}` : ''}`}
    </span>
  );
}

// ---------------------------------------------------------------------------- the two input shapes

interface SyncedInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value: string;
  onValue: (v: string) => void;
  /** Fired when the person is done with the box — blur or Enter. Optional. */
  onCommit?: (v: string) => void;
}

/**
 * The reference's live input: every keystroke writes the model (`bindLayerProps`' `input`
 * listener) and the model writes back — except into the field that has focus, which is what
 * `syncPropsFromLayer` checks before it assigns. Without the draft the panel would reformat «5»
 * into «5.0» under the caret on the way through the model.
 */
function SyncedInput({ value, onValue, onCommit, ...rest }: SyncedInputProps): ReactElement {
  const [draft, setDraft] = useState<string | null>(null);
  /*
   * `onCommit` — for a field whose value has to reach the SERVER.
   *
   * `onValue` fires on every keystroke and is the right shape for the local model. A field that is
   * saved on the server cannot use it: one request per character is both a storm and a race. So the
   * commit is the moment the person is done with the box — blur, or Enter.
   */
  const commit = (v: string): void => {
    if (onCommit) onCommit(v);
  };
  return (
    <input
      {...rest}
      value={draft ?? value}
      onChange={(e) => {
        setDraft(e.target.value);
        onValue(e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        commit((e.target as HTMLInputElement).value);
      }}
      onBlur={(e) => {
        setDraft(null);
        commit(e.target.value);
      }}
    />
  );
}

interface SyncedTextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  value: string;
  onValue: (v: string) => void;
}

function SyncedTextarea({ value, onValue, ...rest }: SyncedTextareaProps): ReactElement {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <textarea
      {...rest}
      value={draft ?? value}
      onChange={(e) => {
        setDraft(e.target.value);
        onValue(e.target.value);
      }}
      onBlur={() => setDraft(null)}
    />
  );
}

interface CommitInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value: string;
  onCommit: (v: string) => void;
  ref?: Ref<HTMLInputElement> | undefined;
}

/**
 * The inputs upstream binds to the DOM `change` event — canvas size, background placement, the
 * validity period. `change` fires on blur or Enter, never per keystroke, and that is load-bearing
 * here: these commits rebuild the canvas or the whole draft, so committing «1» on the way to
 * «1080» would resize the template down to the 16px floor under the operator's hands.
 */
function CommitInput({ value, onCommit, ...rest }: CommitInputProps): ReactElement {
  const [draft, setDraft] = useState<string | null>(null);
  const fire = (): void => {
    const next = draft;
    setDraft(null);
    if (next !== null && next !== value) onCommit(next);
  };
  return (
    <input
      {...rest}
      value={draft ?? value}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={fire}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
    />
  );
}

// ------------------------------------------------------------------------------------ the contract

export interface PropsPanelProps {
  template: DemoTemplate;
  /** `ED.layout` — the working copy, mutated in place by every callback below. */
  layout: EditorLayout;
  /** `ED.sel`. */
  sel: string | null;
  /** `ED.tab`. */
  tab: EditorTab;
  /** `ED.aspectLock` — read by the Canvas tab's lock box. */
  aspectLock: boolean;
  /**
   * `ED.propsKey` — the structural memo key `#propBody` is rebuilt on. The screen computes the
   * same string for its own state; omit it and the panel computes its own (`propsStructureKey`).
   */
  propsKey?: string | undefined;
  /** A layer row was chosen: `ED.sel = id` plus the stage/readout refresh. */
  onSelect(id: string | null): void;
  /** `ED.tab = k; ED.propsKey = ''`. */
  onTab(tab: EditorTab): void;
  onAspectLock(value: boolean): void;
  /** `ED.testData[key] = value` — the sample the stage previews with. */
  onTestData(key: string, value: string): void;
  /**
   * `mut(fn)` — `m08-admin.js:349`: push an undo step, apply, mark the draft dirty, drop
   * `propsKey` and re-render the editor. Everything structural goes through here.
   */
  onMutate(fn: (working: EditorLayout) => void): void;
  /**
   * The live path — upstream's `input` listeners, which mark dirty and redraw the stage WITHOUT
   * pushing an undo step (`m08-admin.js:373`). Optional: without it a keystroke falls back to
   * `onMutate`, which is correct but records one undo step per character. The panel's inputs are
   * controlled, so whichever it lands on has to re-render the panel too.
   */
  onEdit?: ((fn: (working: EditorLayout) => void) => void) | undefined;
  /** `ED.safe = true` — a new safe area turns the stage's overlay on (`m08-admin.js:397`). */
  onShowSafeAreas?: (() => void) | undefined;
  /** `openFontUploadModal(file, layer)` — the dialog is the screen's, the file input is here. */
  onFontFile?: ((file: File, layer: EditorLayer | null) => void) | undefined;
  /** The session's asset library; the seeded one when the screen keeps none of its own. */
  assets?: LibraryAsset[] | undefined;
  /** The session's font library, for the family/weight selects and the «Font dosyası» help line. */
  fonts?: LibraryFont[] | undefined;
  /** How the library read went, so the picker can tell «boş» from «okunamadı». */
  library?: LibraryReadState | undefined;
  /** `ED.editLocale` — the working language. Defaults to Turkish where no screen says. */
  editLocale?: 'tr' | 'en' | undefined;
}

/**
 * What the editor knows about its own library read — the four outcomes, plus a way to ask again.
 *
 * The panel used to be handed arrays and nothing else, so an empty array WAS the answer: a failed
 * read drew a background picker with one option («— arka plan yok —») and a font help line ending
 * «yok», which is what a genuinely empty library looks like. An administrator with a full library in
 * front of a server that stopped answering was told their images are gone.
 */
export interface LibraryReadState {
  status: 'loading' | 'ready' | 'error';
  /** True when what is on screen is an EARLIER answer: kept, but not current. */
  stale: boolean;
  error: { kind: 'session' | 'forbidden' | 'server'; message: string } | null;
  retry: () => void;
}

/**
 * The one notice both tabs draw, with its own id on each so a test can name which one it clicked.
 *
 * Nothing at all in the `ready` state, which is the state the frozen reference is in and the state
 * the visual acceptance measures: the demo's library is a synchronous read of its own seed and never
 * loads, never fails and is never stale.
 */
function LibraryNotice({
  state,
  retryId,
  staleId,
  subject,
}: {
  state: LibraryReadState | undefined;
  retryId: string;
  staleId: string;
  subject: string;
}): ReactElement | null {
  if (!state || state.status === 'ready') return null;
  if (state.status === 'loading') {
    return state.stale ? null : <p className="muted small">{`${subject} okunuyor…`}</p>;
  }
  return (
    <div className="warn-box small" role="alert">
      <b>Kütüphane okunamadı.</b>
      {` ${state.error?.message ?? 'Sunucuya ulaşılamadı'} `}
      {state.stale ? (
        <span id={staleId}>{`Aşağıdaki ${subject} en son okunan listedir; güncel olmayabilir. `}</span>
      ) : null}
      {state.error?.kind === 'session' ? null : (
        <button className="btn btn-secondary btn-sm" type="button" id={retryId} onClick={state.retry}>
          Yeniden dene
        </button>
      )}
    </div>
  );
}

/** What the four tabs are handed: the props, with both libraries and every optional resolved. */
interface PanelContext {
  template: DemoTemplate;
  layout: EditorLayout;
  sel: string | null;
  aspectLock: boolean;
  assets: LibraryAsset[];
  fonts: LibraryFont[];
  /** `mut` — the undo-recording path. */
  commit: (fn: (working: EditorLayout) => void) => void;
  /** The live `input` path; `commit` when the screen has no separate one. */
  edit: (fn: (working: EditorLayout) => void) => void;
  select: (id: string | null) => void;
  setTestValue: (key: string, value: string) => void;
  showSafeAreas: () => void;
  setAspectLock: (value: boolean) => void;
  fontFile: (file: File, layer: EditorLayer | null) => void;
  /** Undefined where no screen said — the demo's panel and every fixture render. */
  library: LibraryReadState | undefined;
  /**
   * The language being edited. Presentation state, owned by the screen: it decides WHICH language's
   * words and artwork the boxes below show, and changing it saves nothing and dirties nothing.
   */
  editLocale: 'tr' | 'en';
}

/** `ED.propsKey` — `m08-admin.js:302`, field for field. */
function structureKeyOf(
  template: DemoTemplate,
  layout: EditorLayout,
  tab: EditorTab,
  sel: string | null,
  families: string[],
  editLocale: 'tr' | 'en' = 'tr',
): string {
  const l = layerById(layout, sel);
  return `${template.id}|${editLocale}|${tab}|${sel}|${layout.layers.length}|${(layout.fieldDefinitions || []).length}|${(layout.safeAreas || []).length}|${families.join(',')}|${l ? `${l.font}${l.align}${l.kind}${l.fitMode}` : ''}|${template.status}|${canvasW(asCanvas(layout.canvas))}x${canvasH(asCanvas(layout.canvas))}`;
}

/**
 * The same key over an `EditorState`.
 *
 * Exported because it is the panel's contract with the screen as much as with itself: anything
 * that changes this string changes the panel's STRUCTURE, and anything that does not must leave
 * the inputs — and the caret — where they are.
 */
export const propsStructureKey = (
  template: DemoTemplate,
  layout: EditorLayout,
  state: EditorState,
  families: string[],
): string => structureKeyOf(template, layout, state.tab, state.sel, families, state.editLocale);

// ------------------------------------------------------------------------------- KATMAN sekmesi

const SAFE_HELP_DEFAULT =
  'Arka planın pikselleri analiz edilir: çizimin içindeki en geniş boş bant bulunur, katman oraya taşınır ve beş örnek değerle çakışma ölçülür. Bu bir yardımcıdır; şablonun şekli hakkında varsayım yapmaz.';

function LayerTab({ ctx }: { ctx: PanelContext }): ReactElement {
  const { layout: lay, sel, assets, fonts } = ctx;
  const toast = useToast();
  /* Upstream writes its progress and its five measured rows straight into `#safeHelp`; the DOM is
     where that transient output lives. Here it is component state, and it resets with the body. */
  const [safeHelp, setSafeHelp] = useState<ReactNode>(SAFE_HELP_DEFAULT);
  const addKind = useRef<HTMLSelectElement>(null);

  const l = layerById(lay, sel);
  const W = canvasW(asCanvas(lay.canvas));
  const isText = !!l && (l.kind === 'text' || l.kind === 'static');
  const list = lay.layers.slice().sort((a, b) => (b.zIndex ?? 10) - (a.zIndex ?? 10));
  /* §11 — a private field may never be bound to a render layer, and an image data type is not a
     text layer's business either. */
  const freeFields = (lay.fieldDefinitions || []).filter(
    (f) => !isImageDataType(f.dataType) && canRender(f.visibility),
  );
  const families = familyList(fonts);
  const weights = weightList(fonts, l?.font);

  const tool = (id: string, apply: (x: EditorLayer, working: EditorLayout) => void) => (): void =>
    ctx.commit((working) => {
      const x = layerById(working, id);
      if (x) apply(x, working);
    });

  const rowSelect =
    (id: string) =>
    (e: ReactMouseEvent | ReactKeyboardEvent): void => {
      /* The six tools sit inside the row, so their click reaches it too (`m08-admin.js:348`). */
      if (e.target instanceof Element && e.target.closest('.lyr-tools')) return;
      ctx.select(id);
    };

  const addLayer = (): void =>
    ctx.commit((working) => {
      const d = working;
      const kind = addKind.current?.value ?? 'text';
      let nl: EditorLayer;
      if (kind === 'qr') nl = qrLayer({ x: 0.88, y: 0.88, width: 0.08 });
      else if (kind === 'image') nl = makeLayer({ kind: 'image', x: 0.5, y: 0.3, width: 0.2, height: 0.1 });
      else if (kind === 'static')
        nl = makeLayer({
          kind: 'static',
          text: 'Sabit metin',
          x: 0.5,
          y: 0.35,
          width: 0.6,
          fontSizeRatio: 0.03,
          minFontSizeRatio: 0.02,
          color: '#000000',
          weight: 500,
        });
      else {
        const free = (d.fieldDefinitions || []).filter(
          (f) => !isGraphicDataType(f.dataType) && canRender(f.visibility),
        );
        nl = textLayer(free[0]?.key ?? 'recipient_name', {
          x: 0.5,
          y: 0.6,
          width: 0.6,
          fontSizeRatio: 0.03,
          minFontSizeRatio: 0.018,
          color: '#000000',
          weight: 500,
        });
      }
      nl.zIndex = Math.max(0, ...d.layers.map((x) => x.zIndex ?? 10)) + 1;
      d.layers.push(nl);
      ctx.select(nl.id);
      /* A QR layer without its field resolves to nothing; the reference adds the field with it. */
      if (kind === 'qr' && !(d.fieldDefinitions || []).some((f) => f.key === 'verification_qr'))
        d.fieldDefinitions.push(makeField('verification_qr'));
    });

  /** The single `[data-l]` listener — `m08-admin.js:373`, branch for branch. */
  const setProp = (k: string, v: string): void =>
    ctx.edit((working) => {
      const layer = layerById(working, sel);
      if (!layer) return;
      const w = canvasW(asCanvas(working.canvas));
      const target = layer as Record<string, unknown>;
      if (k === 'sizePx') {
        const n = Number.parseInt(v, 10);
        if (!Number.isNaN(n) && n > 0) layer.fontSizeRatio = Number((n / w).toFixed(6));
      } else if (k === 'minPx') {
        const n = Number.parseInt(v, 10);
        if (!Number.isNaN(n) && n > 0) layer.minFontSizeRatio = Number((n / w).toFixed(6));
      } else if (k === 'x' || k === 'y' || k === 'width') {
        const n = Number.parseFloat(v);
        if (!Number.isNaN(n)) layer[k] = clamp(n / 100, k === 'width' ? 0.02 : 0, 1.5);
      } else if (k === 'height') {
        const n = Number.parseFloat(v);
        layer.height = v === '' || Number.isNaN(n) ? null : clamp(n / 100, 0.01, 1.5);
      } else if (k === 'lineHeight' || k === 'letterSpacing' || k === 'opacity' || k === 'rotation') {
        const n = Number.parseFloat(v);
        if (!Number.isNaN(n)) layer[k] = n;
      } else if (k === 'zIndex' || k === 'weight' || k === 'maxLines') {
        target[k] = Number.parseInt(v, 10) || 0;
      } else if (k === 'color') {
        layer.color = v;
      } else if (k === 'colorhex') {
        if (/^#[0-9a-f]{6}$/i.test(v)) layer.color = v;
      } else if (k === 'font') {
        layer.font = v;
        /* The family's real faces decide the weight: 600 when it exists, the lightest otherwise. */
        const ws = weightList(fonts, v);
        const kept = ws.includes(600) ? 600 : ws[0];
        if (!ws.includes(layer.weight ?? 0) && kept !== undefined) layer.weight = kept;
      } else {
        /* `assetId` lands here too: the reference writes `l.assetId`, NOT `l.assetRef`
           (`m08-admin.js:379-380`), so choosing another image records the id and leaves the bound
           reference — and the digest line under the select — where they were. Reproduced. */
        target[k] = v;
      }
    });

  /* `document.fonts.load` warms the face before the stage measures again (`m08-admin.js:378`); the
     empty edit after the promise stands in for upstream's `renderStage()` there. */
  const setFont = (v: string): void => {
    setProp('font', v);
    if (document.fonts?.load) {
      document.fonts
        .load(`${layerById(lay, sel)?.weight ?? 400} 40px "${v}"`)
        .then(() => ctx.edit(() => undefined))
        .catch(() => undefined);
    }
  };

  const autoSafe = async (): Promise<void> => {
    const bgRes = resolveRef(lay.background?.assetRef as { assetId?: string } | null | undefined, assets);
    if (!bgRes.ok) {
      toast('Önce arka plan görseli yükleyin');
      return;
    }
    setSafeHelp('Piksel analizi çalışıyor…');
    try {
      const an = await analyzeAsset(bgRes.asset.dataUrlForPrototype, bgRes.asset.id);
      const band = detectSafeBand(an);
      if (!band) {
        setSafeHelp(
          <span style={{ color: 'var(--warn)' }}>Boş bant bulunamadı; katmanı elle konumlandırın.</span>,
        );
        return;
      }
      const cfg = layerFromBand(band, an);
      ctx.commit((working) => {
        const layer = layerById(working, sel);
        if (!layer) return;
        Object.assign(layer, {
          x: cfg.x,
          y: cfg.y,
          width: cfg.w,
          fontSizeRatio: cfg.fontSizeRatio,
          minFontSizeRatio: cfg.minFontSizeRatio,
          lineHeight: cfg.lineHeight,
          maxLines: cfg.maxLines,
        });
        const f = edField(working, layer.fieldKey);
        const sa = makeSafeArea({
          label: `Otomatik alan · ${f ? fieldLabel(f, 'tr') : layer.kind}`,
          ...cfg.safeArea,
        });
        working.safeAreas = (working.safeAreas || []).filter((a) => a.id !== layer.safeAreaId);
        working.safeAreas.push(sa);
        layer.safeAreaId = sa.id;
        ctx.showSafeAreas();
      });
      toast(
        `Güvenli alan bulundu ve kaydedildi: y %${(band.center * 100).toFixed(1)}, yükseklik %${(band.height * 100).toFixed(1)}`,
      );
    } catch (err) {
      setSafeHelp(`Analiz başarısız: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const checkFit = async (): Promise<void> => {
    if (!l) return;
    setSafeHelp('Beş değerle çakışma ölçülüyor…');
    try {
      const vals = lay.subjectType === 'organization' ? TEST_ORGS : TEST_NAMES;
      const r = await verifyLayerPlacement(
        asRenderLayout(lay) as FitCheckLayout,
        asLayer(l) as FitCheckLayer,
        vals,
      );
      setSafeHelp(
        <>
          {r.results.map((x) => (
            <div key={x.value}>
              {`${x.ok ? '✓' : '✕'} ${x.value} — ${x.size}px${x.overflow ? ', sığmıyor' : ''}${
                x.collision ? `, çakışma %${(x.inkRatio * 100).toFixed(1)}` : ', çakışma yok'
              }${x.inSafe === false ? ', güvenli alan dışında' : ''}`}
            </div>
          ))}
          <div className="small muted">
            {r.hasBackground ? 'Arka plan pikselleriyle ölçüldü.' : 'Arka plan yok; yalnız taşma ölçüldü.'}
          </div>
        </>,
      );
    } catch (err) {
      setSafeHelp(`Ölçüm başarısız: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <>
      <div className="section">
        <h4>
          {'Katmanlar '}
          <span className="small muted">{`(${lay.layers.length})`}</span>
        </h4>
        <div className="layer-list">
          {list.map((x) => (
            // biome-ignore lint/a11y/useSemanticElements: the reference's layer row is a div with role="button"; its markup is the acceptance target
            <div
              className={`layer-item ${x.id === sel ? 'is-selected' : ''}`}
              data-sel={x.id}
              role="button"
              tabIndex={0}
              key={x.id}
              onClick={rowSelect(x.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  rowSelect(x.id)(e);
                }
              }}
            >
              <span
                className="sq"
                style={
                  x.kind === 'qr'
                    ? { background: '#000' }
                    : x.kind === 'image'
                      ? { background: '#5FA8D3' }
                      : {}
                }
              />
              <span>{layerName(lay, x)}</span>
              <span className="lyr-tools">
                <button
                  className="icon-btn xs"
                  type="button"
                  data-lock={x.id}
                  title={x.locked ? 'Kilidi aç' : 'Kilitle'}
                  onClick={tool(x.id, (t) => {
                    t.locked = !t.locked;
                  })}
                >
                  {x.locked ? '🔒' : '🔓'}
                </button>
                <button
                  className="icon-btn xs"
                  type="button"
                  data-hide={x.id}
                  title={x.hidden ? 'Göster' : 'Gizle'}
                  onClick={tool(x.id, (t) => {
                    t.hidden = !t.hidden;
                  })}
                >
                  {x.hidden ? '◌' : '●'}
                </button>
                <button
                  className="icon-btn xs"
                  type="button"
                  data-up={x.id}
                  title="Öne al"
                  onClick={tool(x.id, (t) => {
                    t.zIndex = (t.zIndex ?? 10) + 1;
                  })}
                >
                  ↑
                </button>
                <button
                  className="icon-btn xs"
                  type="button"
                  data-down={x.id}
                  title="Arkaya al"
                  onClick={tool(x.id, (t) => {
                    t.zIndex = Math.max(0, (t.zIndex ?? 10) - 1);
                  })}
                >
                  ↓
                </button>
                <button
                  className="icon-btn xs"
                  type="button"
                  data-copyl={x.id}
                  title="Çoğalt"
                  onClick={tool(x.id, (t, working) => {
                    const n = Object.assign(deepClone(t), {
                      id: uid('lyr'),
                      x: clamp(t.x + 0.03, 0, 1),
                      y: clamp(t.y + 0.03, 0, 1),
                      zIndex: (t.zIndex ?? 10) + 1,
                    });
                    working.layers.push(n);
                    ctx.select(n.id);
                  })}
                >
                  ⧉
                </button>
                {lay.layers.length > 1 ? (
                  <button
                    className="icon-btn xs"
                    type="button"
                    data-dell={x.id}
                    title="Sil"
                    onClick={() =>
                      ctx.commit((working) => {
                        working.layers = working.layers.filter((y) => y.id !== x.id);
                        if (!layerById(working, sel)) ctx.select(working.layers[0]?.id ?? null);
                      })
                    }
                  >
                    ✕
                  </button>
                ) : null}
              </span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
          <select
            className="select select-sm"
            id="addKind"
            aria-label="Katman türü"
            style={{ flex: '1', minWidth: '120px' }}
            ref={addKind}
            defaultValue="text"
          >
            <option value="text">Dinamik metin</option>
            <option value="static">Sabit metin</option>
            <option value="qr">QR kod</option>
            <option value="image">Görsel / imza</option>
          </select>
          <button className="btn btn-secondary btn-sm" type="button" id="addLayerBtn" onClick={addLayer}>
            Katman ekle
          </button>
        </div>
      </div>

      {l ? (
        <div className="section">
          <h4>{`Seçili katman · ${layerName(lay, l)}`}</h4>

          {l.kind === 'text' ? (
            <div className="field">
              <label htmlFor="lp_field">Bağlı alan</label>
              <select
                className="select select-sm"
                id="lp_field"
                data-l="fieldKey"
                value={l.fieldKey ?? ''}
                onChange={(e) => setProp('fieldKey', e.target.value)}
              >
                {freeFields.map((f) => (
                  <option value={f.key} key={f.key}>
                    {fieldLabel(f, 'tr')}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {l.kind === 'static' ? (
            <div className="field">
              <label htmlFor="lp_text">Sabit metin</label>
              <SyncedInput
                className="input input-sm"
                id="lp_text"
                data-l="text"
                value={l.text ?? ''}
                onValue={(v) => setProp('text', v)}
              />
            </div>
          ) : null}

          {/*
           * THE SAME LITERAL IN THE LANGUAGE BEING EDITED.
           *
           * Drawn only while a language other than the template's own is selected, because the box
           * above IS the template's own language: the server seals the declared language's document
           * from `style.text` and every other sealed language's from `style.textLocalized[locale]`.
           * So there is one box per language and no box that does nothing.
           *
           * Empty is not «the same words»: a sealed language with nothing here is refused at publish
           * by name, and the Şablon tab lists it. Nothing is copied over for the author.
           */}
          {l.kind === 'static' && ctx.editLocale !== lay.defaultLanguage ? (
            <div className="field" data-hpw-locale-text={ctx.editLocale}>
              <label htmlFor="lp_textLoc">
                {ctx.editLocale === 'en' ? 'Static text (English)' : 'Sabit metin (Türkçe)'}
              </label>
              <SyncedInput
                className="input input-sm"
                id="lp_textLoc"
                data-l={`textLocalized.${ctx.editLocale}`}
                data-testid="editor-locale-text"
                value={l.textLocalized?.[ctx.editLocale] ?? ''}
                onValue={(v) =>
                  ctx.edit((working) => {
                    const target = working.layers.find((x) => x.id === l.id);
                    if (!target) return;
                    const bag = { ...(target.textLocalized ?? {}) };
                    if (v.trim() === '') delete bag[ctx.editLocale];
                    else bag[ctx.editLocale] = v;
                    target.textLocalized = bag;
                  })
                }
              />
              <div className="help">
                {`«${l.text ?? ''}» yerine ${localeLabel(ctx.editLocale)} belgede bu çizilir. Boş bırakılırsa bu dil mühürlenemez; `}
                {'yazı tipi, punto, renk ve konum iki dilde ortaktır.'}
              </div>
            </div>
          ) : null}

          {l.kind === 'image' ? (
            <>
              <div className="field">
                <label htmlFor="lp_asset">Görsel</label>
                <select
                  className="select select-sm"
                  id="lp_asset"
                  data-l="assetId"
                  value={l.assetRef?.assetId ?? ''}
                  onChange={(e) => setProp('assetId', e.target.value)}
                >
                  <option value="">— seçilmedi —</option>
                  {assets.map((a) => (
                    <option value={a.id} key={a.id}>
                      {`${a.originalFileName || a.id} · ${a.width}×${a.height}`}
                    </option>
                  ))}
                </select>
                <span className="help">
                  {l.assetRef ? `digest ${String(l.assetRef.sha256).slice(0, 12)}…` : 'digest referansı yok'}
                </span>
              </div>
              <div className="field">
                <label htmlFor="lp_imgfit">Yerleşim</label>
                <select
                  className="select select-sm"
                  id="lp_imgfit"
                  data-l="imageFit"
                  value={l.imageFit || 'contain'}
                  onChange={(e) => setProp('imageFit', e.target.value)}
                >
                  {FIT_MODES.filter((x) => ['contain', 'cover', 'stretch', 'crop'].includes(x.key)).map(
                    (x) => (
                      <option value={x.key} key={x.key}>
                        {x.tr}
                      </option>
                    ),
                  )}
                </select>
              </div>
            </>
          ) : null}

          {isText ? (
            <>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="lp_font">Font</label>
                  <select
                    className="select select-sm"
                    id="lp_font"
                    data-l="font"
                    value={l.font ?? ''}
                    onChange={(e) => setFont(e.target.value)}
                  >
                    {families.map((f) => (
                      <option value={f} key={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="lp_weight">Kalınlık</label>
                  <select
                    className="select select-sm"
                    id="lp_weight"
                    data-l="weight"
                    value={String(l.weight ?? '')}
                    onChange={(e) => setProp('weight', e.target.value)}
                  >
                    {weights.map((w) => (
                      <option value={w} key={w}>
                        {w}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="lp_size">{`Punto (px @${W})`}</label>
                  <SyncedInput
                    className="input input-sm"
                    id="lp_size"
                    data-l="sizePx"
                    type="number"
                    min="4"
                    max="1200"
                    value={String(sizePx(asLayer(l), asCanvas(lay.canvas)))}
                    onValue={(v) => setProp('sizePx', v)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="lp_min">Minimum punto</label>
                  <SyncedInput
                    className="input input-sm"
                    id="lp_min"
                    data-l="minPx"
                    type="number"
                    min="4"
                    max="1200"
                    value={String(minPx(asLayer(l), asCanvas(lay.canvas)))}
                    onValue={(v) => setProp('minPx', v)}
                  />
                </div>
              </div>

              <div className="field">
                <label htmlFor="lp_color">Renk</label>
                <div className="color-field">
                  <input
                    type="color"
                    id="lp_color"
                    data-l="color"
                    value={l.color ?? '#000000'}
                    aria-label="Renk seçici"
                    onChange={(e) => setProp('color', e.target.value)}
                  />
                  {/* Only a complete `#rrggbb` reaches the model (`m08-admin.js:377`); the draft is
                      what keeps a half-typed value visible until it is one. */}
                  <SyncedInput
                    className="input input-sm"
                    data-l="colorhex"
                    value={l.color ?? ''}
                    aria-label="Renk (hex)"
                    onValue={(v) => setProp('colorhex', v)}
                  />
                </div>
              </div>

              <div className="field">
                {/* biome-ignore lint/a11y/noLabelWithoutControl: the reference's caption for the button group below, which carries its own labels */}
                <label>Hizalama</label>
                <div className="align-group">
                  {(['left', 'center', 'right'] as const).map((a) => (
                    <button
                      type="button"
                      data-align={a}
                      className={l.align === a ? 'is-on' : ''}
                      aria-label={ALIGN_LABEL[a]}
                      key={a}
                      onClick={() =>
                        ctx.commit((working) => {
                          const layer = layerById(working, sel);
                          if (layer) layer.align = a;
                        })
                      }
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <path d={ALIGN_ICON[a]} />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="lp_fit">Uzun metin</label>
                  <select
                    className="select select-sm"
                    id="lp_fit"
                    data-l="fitMode"
                    value={l.fitMode ?? ''}
                    onChange={(e) => setProp('fitMode', e.target.value)}
                  >
                    <option value="shrink">Otomatik küçült</option>
                    <option value="fixed">Sabit punto</option>
                    <option value="wrap">Satıra sar</option>
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="lp_maxLines">En çok satır</label>
                  <select
                    className="select select-sm"
                    id="lp_maxLines"
                    data-l="maxLines"
                    value={String(l.maxLines || 1)}
                    onChange={(e) => setProp('maxLines', e.target.value)}
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option value={n} key={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="lp_lh">Satır yüksekliği</label>
                  <SyncedInput
                    className="input input-sm"
                    id="lp_lh"
                    data-l="lineHeight"
                    type="number"
                    step="0.05"
                    min="0.8"
                    max="2.5"
                    value={String(l.lineHeight ?? 1.15)}
                    onValue={(v) => setProp('lineHeight', v)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="lp_ls">Harf aralığı (%)</label>
                  <SyncedInput
                    className="input input-sm"
                    id="lp_ls"
                    data-l="letterSpacing"
                    type="number"
                    step="0.5"
                    min="-10"
                    max="50"
                    value={String(l.letterSpacing ?? 0)}
                    onValue={(v) => setProp('letterSpacing', v)}
                  />
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="lp_tt">Büyük/küçük harf</label>
                  <select
                    className="select select-sm"
                    id="lp_tt"
                    data-l="textTransform"
                    value={l.textTransform || 'none'}
                    onChange={(e) => setProp('textTransform', e.target.value)}
                  >
                    <option value="none">Olduğu gibi</option>
                    <option value="uppercase">BÜYÜK (TR İ/ı)</option>
                    <option value="lowercase">küçük</option>
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="lp_va">Dikey hizalama</label>
                  <select
                    className="select select-sm"
                    id="lp_va"
                    data-l="verticalAlign"
                    value={l.verticalAlign || 'middle'}
                    onChange={(e) => setProp('verticalAlign', e.target.value)}
                  >
                    <option value="top">Üst</option>
                    <option value="middle">Orta</option>
                    <option value="bottom">Alt</option>
                  </select>
                </div>
              </div>
            </>
          ) : null}

          <div className="field-row">
            <div className="field">
              <label htmlFor="lp_x">X (%)</label>
              <SyncedInput
                className="input input-sm"
                id="lp_x"
                data-l="x"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(l.x * 100).toFixed(1)}
                onValue={(v) => setProp('x', v)}
              />
            </div>
            <div className="field">
              <label htmlFor="lp_y">Y (%)</label>
              <SyncedInput
                className="input input-sm"
                id="lp_y"
                data-l="y"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={(l.y * 100).toFixed(1)}
                onValue={(v) => setProp('y', v)}
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="lp_w">Genişlik (%)</label>
              <SyncedInput
                className="input input-sm"
                id="lp_w"
                data-l="width"
                type="number"
                step="0.1"
                min="2"
                max="150"
                value={(l.width * 100).toFixed(1)}
                onValue={(v) => setProp('width', v)}
              />
            </div>
            <div className="field">
              <label htmlFor="lp_h">Yükseklik (%)</label>
              <SyncedInput
                className="input input-sm"
                id="lp_h"
                data-l="height"
                type="number"
                step="0.1"
                min="0"
                max="150"
                value={l.height ? (l.height * 100).toFixed(1) : ''}
                placeholder="otomatik"
                onValue={(v) => setProp('height', v)}
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="lp_rot">Açı (°)</label>
              <SyncedInput
                className="input input-sm"
                id="lp_rot"
                data-l="rotation"
                type="number"
                step="1"
                min="-180"
                max="180"
                value={String(l.rotation || 0)}
                onValue={(v) => setProp('rotation', v)}
              />
            </div>
            <div className="field">
              <label htmlFor="lp_z">Katman sırası (z)</label>
              <SyncedInput
                className="input input-sm"
                id="lp_z"
                data-l="zIndex"
                type="number"
                step="1"
                min="0"
                max="999"
                value={String(l.zIndex ?? 10)}
                onValue={(v) => setProp('zIndex', v)}
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="lp_op">Opaklık</label>
              <SyncedInput
                className="input input-sm"
                id="lp_op"
                data-l="opacity"
                type="number"
                step="0.05"
                min="0.05"
                max="1"
                value={String(l.opacity ?? 1)}
                onValue={(v) => setProp('opacity', v)}
              />
            </div>
            <div className="field">
              <label htmlFor="lp_safe">Güvenli alan</label>
              <select
                className="select select-sm"
                id="lp_safe"
                data-l="safeAreaId"
                value={l.safeAreaId ?? ''}
                onChange={(e) => setProp('safeAreaId', e.target.value)}
              >
                <option value="">— bağlı değil —</option>
                {(lay.safeAreas || []).map((a) => (
                  <option value={a.id} key={a.id}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isText ? (
            <div className="field">
              {/* biome-ignore lint/a11y/noLabelWithoutControl: the reference's caption for the two chips below */}
              <label>Yerleşim yardımcıları</label>
              <div className="chips">
                <button
                  className="chip"
                  type="button"
                  id="autoSafe"
                  onClick={() => {
                    void autoSafe();
                  }}
                >
                  Güvenli alanı otomatik bul
                </button>
                <button
                  className="chip"
                  type="button"
                  id="checkFit"
                  onClick={() => {
                    void checkFit();
                  }}
                >
                  Çakışmayı ölç (5 değer)
                </button>
              </div>
              <div className="help" id="safeHelp">
                {safeHelp}
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="section">
          <p className="muted small">Katman seçilmedi.</p>
        </div>
      )}

      <div className="section">
        <h4>Font dosyası</h4>
        <label className="btn btn-secondary btn-sm" htmlFor="fontUpload" style={{ justifySelf: 'start' }}>
          Font yükle (.ttf / .otf / .woff / .woff2)
        </label>
        <input
          type="file"
          id="fontUpload"
          accept=".ttf,.otf,.woff,.woff2"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) ctx.fontFile(f, l);
            e.target.value = '';
          }}
        />
        <LibraryNotice
          state={ctx.library}
          retryId="edFontRetry"
          staleId="edFontStale"
          subject="font kütüphanesi"
        />
        <div className="help">
          {/*
           * The REGISTERED families — three states, the same three the notice above draws.
           *
           * `familyList` and `weightList` fall back to «Poppins» and 400/500/600/700 when the
           * library holds nothing, because the prototype's SELECTS must always offer a choice. This
           * sentence is not a select: it claims those values are registered font ASSETS, and on a
           * fresh installation none of them is. It read «Kayıtlı font asset'leri: Poppins
           * (400/500/600/700)» over an empty library, which is what made an editor correctly
           * refusing to export look like a broken typeface rather than an unfinished setup.
           *
           * A read that FAILED is still not an empty library (that is what this whole panel was
           * fixed for), so it says neither «yok» nor a list; the notice above it carries the
           * failure and the way to ask again.
           */}
          {`Kayıtlı font asset'leri: ${
            ctx.library && ctx.library.status === 'error' && !ctx.library.stale
              ? 'okunamadı'
              : [...new Set(fonts.map((f) => f.family))]
                  .map((f) => `${f} (${weightList(fonts, f).join('/')})`)
                  .join(', ') || 'yok'
          }. Release yalnız tam font digest'ine bağlanır; fallback font yayın engelidir.`}
        </div>
      </div>
    </>
  );
}

// ------------------------------------------------------------------------------- CANVAS sekmesi

/**
 * One language's own certificate visual, for a language that is not the one the template declares.
 *
 * Its own component rather than more branches inside `CanvasTab`, because what it edits is a
 * different place in the draft: `localizedBackground[locale]`, which is what the server loads and
 * verifies when it seals that language's document. Everything else about the artwork — fit, focal
 * point, crop, scale, opacity, rotation — stays the release's own and is edited above.
 */
function LocaleBackgroundSection({ ctx }: { ctx: PanelContext }): ReactElement {
  const { layout: lay, assets } = ctx;
  const locale = ctx.editLocale;
  const entry = lay.localizedBackground?.[locale] ?? null;
  const ref = (entry as { assetRef?: { assetId?: string; sha256?: string } } | null)?.assetRef ?? null;
  const res = ref ? resolveRef(ref, assets) : null;
  const a = res?.ok ? res.asset : null;
  const own = (lay.background ?? {}) as { assetRef?: { assetId?: string; sha256?: string } | null };
  const ownRef = own.assetRef ?? null;
  const sameAsOwn = !!ref && !!ownRef && ref.assetId === ownRef.assetId;

  /**
   * Writes ONE language's entry and never the other's, so one language cannot overwrite another.
   *
   * Through the layout module's own writer, which is also what the top bar's «Arka plan yükle» uses:
   * this slot has exactly one writer, so the picker and the upload cannot drift apart again.
   */
  const put = (assetId: string | null): void =>
    ctx.commit((working) => {
      const chosen = assetId ? assets.find((x) => x.id === assetId) : null;
      const ref = assetRefOf(chosen ?? null);
      setLocaleBackground(
        working,
        locale,
        ref === null
          ? null
          : { assetRef: ref, ...(chosen?.hasAlpha === undefined ? {} : { hasAlpha: chosen.hasAlpha }) },
      );
    });

  return (
    <div className="section" data-hpw-locale-bg={locale}>
      <h4>
        {locale === 'en'
          ? 'Certificate visual / background — English'
          : 'Sertifika görseli / arka plan — Türkçe'}
      </h4>
      <LibraryNotice
        state={ctx.library}
        retryId="edLocBgRetry"
        staleId="edLocBgStale"
        subject="görsel kütüphanesi"
      />
      {a ? (
        <div className="file-chip">
          <IconCheck />
          <span>
            {`${a.originalFileName || a.id} · ${a.width}×${a.height}${alphaNote(a)}`}
            <br />
            <span className="small muted mono">{`${String(a.sha256).slice(0, 24)}…`}</span>
          </span>
        </div>
      ) : ref ? (
        <div className="warn-box small" id="locBgUnresolved">
          {`Bu dilin arka plan referansı çözülemedi (${res && !res.ok ? res.reason : 'bilinmiyor'}): `}
          <span className="mono">{ref.assetId}</span>
          {'. Render fail-closed davranır.'}
        </div>
      ) : (
        <div className="warn-box small" id="locBgMissing">
          {`${localeLabel(locale)} için sertifika görseli seçilmedi. Bu dil mühürleniyorsa yayın reddedilir; `}
          {'diğer dilin görseli bu dilin belgesine çizilmez.'}
        </div>
      )}
      <div className="field">
        <label htmlFor="locBg_asset">Kütüphaneden seç</label>
        <select
          className="select select-sm"
          id="locBg_asset"
          data-testid="editor-locale-bg"
          value={ref?.assetId ?? ''}
          onChange={(e) => put(e.target.value || null)}
        >
          <option value="">— bu dilde görsel yok —</option>
          {assets.map((x) => (
            <option value={x.id} key={x.id}>
              {`${x.originalFileName || x.id} · ${x.width}×${x.height}`}
            </option>
          ))}
        </select>
        {assets.length === 0 && ctx.library?.status === 'ready' ? (
          <div className="help">Kütüphanede görsel yok. Görsel Kütüphanesi ekranından yükleyin.</div>
        ) : null}
      </div>
      <div className="row">
        <button
          className="btn btn-ghost btn-sm"
          type="button"
          id="locBg_same"
          data-testid="editor-locale-bg-same"
          disabled={!ownRef || sameAsOwn}
          onClick={() => put(ownRef?.assetId ?? null)}
        >
          Bu dilde de aynı görseli kullan
        </button>
        {ref ? (
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            id="locBg_clear"
            data-testid="editor-locale-bg-clear"
            onClick={() => put(null)}
          >
            Bu dilin görselini kaldır
          </button>
        ) : null}
      </div>
      <div className="help">
        {'Yerleşim, odak, kırpma, ölçek ve opaklık «Arka plan» bölümünden gelir: iki dilde tek geometri, '}
        {'iki görsel. Burada seçilen görsel yalnız '}
        {localeLabel(locale)}
        {' belgesine ve o dilin LinkedIn otomatik kartına çizilir; diğer dilin görselini değiştirmez.'}
        {sameAsOwn ? ' Şu anda iki dil aynı görseli kullanıyor — bu açık bir tercihtir.' : ''}
      </div>
    </div>
  );
}

function CanvasTab({ ctx }: { ctx: PanelContext }): ReactElement {
  const { layout: lay, assets } = ctx;
  const toast = useToast();
  const c = lay.canvas;
  const bg = (lay.background ?? {}) as {
    assetRef?: { assetId?: string; sha256?: string } | null;
    fitMode?: string;
    x?: number;
    y?: number;
    scale?: number;
    opacity?: number;
  };
  const bgRes = bg.assetRef ? resolveRef(bg.assetRef, assets) : null;
  const a = bgRes?.ok ? bgRes.asset : null;
  const wI = useRef<HTMLInputElement>(null);
  const hI = useRef<HTMLInputElement>(null);

  const apply = (w: number, h: number): void =>
    ctx.commit((working) => {
      working.canvas = makeCanvas({ width: w, height: h, outputScale: c.outputScale, unit: c.unit });
    });

  /** `commit` — `bindCanvasProps`, `m08-admin.js:438`: the locked axis follows the typed one. */
  const commitSize = (src: 'w' | 'h'): void => {
    let w = Number.parseInt(wI.current?.value ?? '', 10) || canvasW(asCanvas(c));
    let h = Number.parseInt(hI.current?.value ?? '', 10) || canvasH(asCanvas(c));
    if (ctx.aspectLock) {
      const ar = canvasW(asCanvas(c)) / canvasH(asCanvas(c));
      if (src === 'w') h = Math.round(w / ar);
      else w = Math.round(h * ar);
    }
    apply(clamp(w, 16, 8000), clamp(h, 16, 8000));
  };

  /* `bgm` — `m08-admin.js:449`. Upstream dereferences `lay.background` directly; the record is
     minted first here so a draft that lost one cannot throw mid-edit. */
  const bgm = (fn: (background: Record<string, unknown>) => void): void =>
    ctx.commit((working) => {
      const record = (working.background ?? {}) as Record<string, unknown>;
      working.background = record;
      fn(record);
    });

  return (
    <>
      <div className="section">
        <h4>Canvas ölçüsü</h4>
        <div className="field">
          <label htmlFor="cv_preset">Hazır ölçü</label>
          <select
            className="select select-sm"
            id="cv_preset"
            value={
              CANVAS_PRESETS.find(
                (p) => p.width === canvasW(asCanvas(c)) && p.height === canvasH(asCanvas(c)),
              )?.key ?? 'native'
            }
            onChange={(e) => {
              const p = CANVAS_PRESETS.find((x) => x.key === e.target.value);
              if (p?.width && p.height) apply(p.width, p.height);
              else if (e.target.value === 'native') {
                const r0 = bg.assetRef ? resolveRef(bg.assetRef, assets) : null;
                if (r0?.ok) apply(r0.asset.width, r0.asset.height);
                else toast('Önce arka plan yükleyin');
              }
            }}
          >
            {CANVAS_PRESETS.map((p) => (
              <option value={p.key} key={p.key}>
                {p.tr}
              </option>
            ))}
          </select>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="cv_w">Genişlik (px)</label>
            <CommitInput
              className="input input-sm"
              id="cv_w"
              type="number"
              min="16"
              max="8000"
              ref={wI}
              value={String(canvasW(asCanvas(c)))}
              onCommit={() => commitSize('w')}
            />
          </div>
          <div className="field">
            <label htmlFor="cv_h">Yükseklik (px)</label>
            <CommitInput
              className="input input-sm"
              id="cv_h"
              type="number"
              min="16"
              max="8000"
              ref={hI}
              value={String(canvasH(asCanvas(c)))}
              onCommit={() => commitSize('h')}
            />
          </div>
        </div>
        <label className="check">
          <input
            type="checkbox"
            id="cv_lock"
            checked={ctx.aspectLock}
            onChange={(e) => ctx.setAspectLock(e.target.checked)}
          />
          {` En-boy oranını kilitle (${c.aspectRatio})`}
        </label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            id="cv_swap"
            onClick={() => apply(canvasH(asCanvas(c)), canvasW(asCanvas(c)))}
          >
            {`Yönü çevir (${c.orientation})`}
          </button>
          {a ? (
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              id="cv_native"
              onClick={() => apply(a.width, a.height)}
            >
              {`Görselin özgün ölçüsüne al (${a.width}×${a.height})`}
            </button>
          ) : null}
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            id="cv_scale"
            onClick={() =>
              ctx.commit((working) => {
                working.canvas = makeCanvas({
                  width: canvasW(asCanvas(c)),
                  height: canvasH(asCanvas(c)),
                  outputScale: c.outputScale >= 3 ? 1 : c.outputScale + 1,
                });
              })
            }
          >
            {`Çıktı ölçeği: ${c.outputScale}×`}
          </button>
        </div>
        <div className="help">
          Katman koordinatları 0–1 oranıyla saklanır; canvas ölçüsü değişince alanlar göreli konumlarını
          korur.
        </div>
      </div>

      <div className="section">
        <h4>Arka plan</h4>
        <LibraryNotice
          state={ctx.library}
          retryId="edLibRetry"
          staleId="edLibStale"
          subject="görsel kütüphanesi"
        />
        {a ? (
          <div className="file-chip">
            <IconCheck />
            <span>
              {`${a.originalFileName || a.id} · ${a.width}×${a.height}${alphaNote(a)}`}
              <br />
              <span className="small muted mono">{`${String(a.sha256).slice(0, 24)}…`}</span>
            </span>
          </div>
        ) : bg.assetRef ? (
          <div className="warn-box small">
            {`Arka plan referansı çözülemedi (${bgRes && !bgRes.ok ? bgRes.reason : 'bilinmiyor'}): `}
            <span className="mono">{bg.assetRef.assetId}</span>
            {'. Render fail-closed davranır.'}
          </div>
        ) : (
          <p className="muted small">Arka plan seçilmedi.</p>
        )}
        <div className="field">
          <label htmlFor="bg_asset">Kütüphaneden seç</label>
          <select
            className="select select-sm"
            id="bg_asset"
            value={bg.assetRef?.assetId ?? ''}
            onChange={(e) =>
              bgm((background) => {
                const a2 = e.target.value ? assets.find((x) => x.id === e.target.value) : null;
                background.assetRef = assetRefOf(a2 ?? null);
                /* Recorded with the choice, because this is the last place that holds the decoded
                   asset; the public page reads it back out of the release. */
                if (a2) background.hasAlpha = a2.hasAlpha;
                else delete background.hasAlpha;
              })
            }
          >
            <option value="">— arka plan yok —</option>
            {assets.map((x) => (
              <option value={x.id} key={x.id}>
                {`${x.originalFileName || x.id} · ${x.width}×${x.height}`}
              </option>
            ))}
          </select>
          {/* «Boş» and «okunamadı» are different facts and the picker looked identical in both. */}
          {assets.length === 0 && ctx.library?.status === 'ready' ? (
            <div className="help">Kütüphanede görsel yok. Görsel Kütüphanesi ekranından yükleyin.</div>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="bg_fit">Yerleşim</label>
          <select
            className="select select-sm"
            id="bg_fit"
            value={bg.fitMode ?? ''}
            onChange={(e) =>
              bgm((background) => {
                background.fitMode = e.target.value;
              })
            }
          >
            {FIT_MODES.map((f) => (
              <option value={f.key} key={f.key}>
                {f.tr}
              </option>
            ))}
          </select>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="bg_x">Merkez X (%)</label>
            <CommitInput
              className="input input-sm"
              id="bg_x"
              type="number"
              step="1"
              min="-100"
              max="200"
              value={String(Math.round((bg.x ?? 0.5) * 100))}
              onCommit={(v) =>
                bgm((background) => {
                  background.x = (Number.parseFloat(v) || 0) / 100;
                })
              }
            />
          </div>
          <div className="field">
            <label htmlFor="bg_y">Merkez Y (%)</label>
            <CommitInput
              className="input input-sm"
              id="bg_y"
              type="number"
              step="1"
              min="-100"
              max="200"
              value={String(Math.round((bg.y ?? 0.5) * 100))}
              onCommit={(v) =>
                bgm((background) => {
                  background.y = (Number.parseFloat(v) || 0) / 100;
                })
              }
            />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="bg_scale">Ölçek</label>
            <CommitInput
              className="input input-sm"
              id="bg_scale"
              type="number"
              step="0.01"
              min="0.05"
              max="10"
              value={String(bg.scale ?? 1)}
              onCommit={(v) =>
                bgm((background) => {
                  background.scale = Number.parseFloat(v) || 1;
                })
              }
            />
          </div>
          <div className="field">
            <label htmlFor="bg_op">Opaklık</label>
            <CommitInput
              className="input input-sm"
              id="bg_op"
              type="number"
              step="0.05"
              min="0.05"
              max="1"
              value={String(bg.opacity ?? 1)}
              onCommit={(v) =>
                bgm((background) => {
                  background.opacity = Number.parseFloat(v) || 1;
                })
              }
            />
          </div>
        </div>
        <div className="help">
          «Esnet» varsayılan değildir; oranı bozan yerleşim yalnız açık tercihle uygulanır.
        </div>
      </div>

      {/*
       * THE SECOND LANGUAGE'S ARTWORK.
       *
       * Drawn only while another language is being edited, because the section above IS the declared
       * language's artwork: the server's own model draws the declared language's document on
       * `background` and every other sealed language's on its own entry. One picture per language,
       * one shared placement — a certificate whose fixed words are pixels in the backdrop cannot be
       * translated by translating titles.
       *
       * Nothing is copied for the author. «Bu dilde de aynı görseli kullan» exists so that pointing a
       * language at the SAME asset is a choice they make and can see, not something that happened to
       * them.
       */}
      {ctx.editLocale !== lay.defaultLanguage ? <LocaleBackgroundSection ctx={ctx} /> : null}

      <div className="section">
        <h4>
          {'Güvenli alanlar '}
          <span className="small muted">{`(${(lay.safeAreas || []).length})`}</span>
        </h4>
        {(lay.safeAreas || []).length ? (
          (lay.safeAreas || []).map((a2) => (
            <div className="layer-item" key={a2.id}>
              <span>{a2.label}</span>
              <span className="small muted" style={{ marginLeft: 'auto' }}>
                {`${(a2.x * 100).toFixed(0)},${(a2.y * 100).toFixed(0)} · ${(a2.width * 100).toFixed(0)}×${(a2.height * 100).toFixed(0)}%`}
              </span>
              <button
                className="icon-btn xs"
                type="button"
                data-delsafe={a2.id}
                title="Sil"
                onClick={() =>
                  ctx.commit((working) => {
                    working.safeAreas = (working.safeAreas || []).filter((s) => s.id !== a2.id);
                    /* A layer bound to the deleted area is unbound, never left pointing at it. */
                    for (const l of working.layers) if (l.safeAreaId === a2.id) l.safeAreaId = null;
                  })
                }
              >
                ✕
              </button>
            </div>
          ))
        ) : (
          <p className="muted small">Tanımlı güvenli alan yok — zorunlu değildir.</p>
        )}
        <button
          className="btn btn-secondary btn-sm"
          type="button"
          id="addSafe"
          style={{ justifySelf: 'start', marginTop: '6px' }}
          onClick={() =>
            ctx.commit((working) => {
              const areas = working.safeAreas || [];
              working.safeAreas = areas;
              areas.push(
                makeSafeArea({
                  label: `Güvenli alan ${areas.length + 1}`,
                  x: 0.1,
                  y: 0.4,
                  width: 0.8,
                  height: 0.15,
                }),
              );
              ctx.showSafeAreas();
            })
          }
        >
          Güvenli alan ekle
        </button>
      </div>
    </>
  );
}

// ------------------------------------------------------------------------------ ALANLAR sekmesi

/** `validationRuleErrors` — `m08-admin.js:486`. */
const validationRuleErrors = (f: DemoFieldDefinition): SchemaError[] =>
  validateValidationConfig(f).concat(validateDefaultValue(f));

interface RuleEditorProps {
  field: DemoFieldDefinition;
  onRule: (prop: string, raw: string) => void;
}

/**
 * §13 — the validation rule editor (`validationRuleEditor`, `m08-admin.js:489`).
 *
 * Only SAFE presets are offered; there is no free regex field (§1.3, ReDoS). The rules are
 * narrowed by data type, and a broken bound is printed inline the moment it is typed — the error
 * is not held back until publish.
 */
function RuleEditor({ field: f, onRule }: RuleEditorProps): ReactElement {
  const t = f.dataType || 'string';
  const allowed = VALIDATION_PROPS_BY_TYPE[t] || [];
  const V = (f.validation || {}) as Record<string, unknown>;
  const errs = validationRuleErrors(f);
  const preset = PATTERN_PRESETS[String(V.patternPreset ?? 'none')];
  /* Upstream re-renders nothing but the error list and the summary inside this `<details>`
     (`m08-admin.js:538`), so whether it stands open is decided once, by the errors it opened with. */
  const [initiallyOpen] = useState(() => errs.length > 0);

  const num = (prop: string, label: string): ReactElement => (
    // biome-ignore lint/a11y/noLabelWithoutControl: the input is the `SyncedInput` this label wraps
    <label className="field inline" style={{ margin: '0', flex: '1' }} key={prop}>
      <span className="small muted">{label}</span>
      <SyncedInput
        className="input input-sm"
        type="number"
        data-vr={prop}
        data-k={f.key}
        value={V[prop] === undefined || V[prop] === null ? '' : String(V[prop])}
        aria-label={`${fieldLabel(f, 'tr')} ${label}`}
        onValue={(v) => onRule(prop, v)}
      />
    </label>
  );

  const dat = (prop: string, label: string): ReactElement => (
    // biome-ignore lint/a11y/noLabelWithoutControl: the input is the `SyncedInput` this label wraps
    <label className="field inline" style={{ margin: '0', flex: '1' }} key={prop}>
      <span className="small muted">{label}</span>
      <SyncedInput
        className="input input-sm"
        type={t === 'datetime' ? 'text' : 'date'}
        data-vr={prop}
        data-k={f.key}
        value={V[prop] ? String(V[prop]) : ''}
        placeholder={t === 'datetime' ? 'YYYY-AA-GGTss:dd:ssZ' : ''}
        aria-label={`${fieldLabel(f, 'tr')} ${label}`}
        onValue={(v) => onRule(prop, v)}
      />
    </label>
  );

  const parts: ReactElement[] = [];
  if (allowed.includes('patternPreset'))
    parts.push(
      <label className="field inline" style={{ margin: '0', flex: '1' }} key="patternPreset">
        <span className="small muted">Biçim kuralı</span>
        <select
          className="select select-sm"
          data-vr="patternPreset"
          data-k={f.key}
          aria-label={`${fieldLabel(f, 'tr')} biçim kuralı`}
          value={String(V.patternPreset ?? 'none')}
          onChange={(e) => onRule('patternPreset', e.target.value)}
        >
          {PATTERN_PRESET_KEYS.map((p) => (
            <option value={p} key={p}>
              {PATTERN_PRESETS[p]?.tr}
            </option>
          ))}
        </select>
      </label>,
    );
  if (allowed.includes('minLength')) parts.push(num('minLength', 'En az karakter'));
  if (allowed.includes('maxLength')) parts.push(num('maxLength', 'En çok karakter'));
  if (allowed.includes('min')) parts.push(num('min', 'En küçük değer'));
  if (allowed.includes('max')) parts.push(num('max', 'En büyük değer'));
  if (allowed.includes('dateMin')) parts.push(dat('dateMin', 'En erken tarih'));
  if (allowed.includes('dateMax')) parts.push(dat('dateMax', 'En geç tarih'));
  if (allowed.includes('urlPolicy'))
    parts.push(
      <label className="field inline" style={{ margin: '0', flex: '1' }} key="urlPolicy">
        <span className="small muted">Adres politikası</span>
        <select
          className="select select-sm"
          data-vr="urlPolicy"
          data-k={f.key}
          aria-label={`${fieldLabel(f, 'tr')} adres politikası`}
          value={String(V.urlPolicy ?? (t === 'credential_url' ? 'public_credential' : 'external'))}
          onChange={(e) => onRule('urlPolicy', e.target.value)}
        >
          {URL_POLICY_KEYS.filter((x) => x !== 'dev').map((x) => (
            <option value={x} key={x}>
              {URL_POLICY_TR[x] || x}
            </option>
          ))}
        </select>
      </label>,
    );

  const defined = !!f.validation || f.defaultValue !== undefined;

  return (
    <details className="fld-rules" open={initiallyOpen}>
      <summary className="small">
        {errs.length ? (
          <>
            {'Doğrulama kuralları '}
            <span className="pill pill-danger none">{`${errs.length} hata`}</span>
          </>
        ) : defined ? (
          <>
            {'Doğrulama kuralları '}
            <span className="pill pill-info none">tanımlı</span>
          </>
        ) : (
          'Doğrulama kuralları'
        )}
      </summary>
      {parts.length ? (
        <div className="row" style={{ flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
          {parts}
        </div>
      ) : (
        <p className="muted small" style={{ margin: '6px 0 0' }}>
          Bu veri türü için ayarlanabilir kural yok.
        </p>
      )}
      <div className="row" style={{ marginTop: '6px' }}>
        {/* biome-ignore lint/a11y/noLabelWithoutControl: the input is the `SyncedInput` this label wraps */}
        <label className="field inline" style={{ margin: '0', flex: '1' }}>
          <span className="small muted">Varsayılan değer (boş bırakılırsa yok)</span>
          <SyncedInput
            className="input input-sm"
            data-vr="defaultValue"
            data-k={f.key}
            value={f.defaultValue === undefined || f.defaultValue === null ? '' : String(f.defaultValue)}
            aria-label={`${fieldLabel(f, 'tr')} varsayılan değer`}
            onValue={(v) => onRule('defaultValue', v)}
          />
        </label>
      </div>
      <div className="vr-errors" data-vrerr={f.key}>
        {errs.map((e) => (
          <div className="warn-box small" style={{ marginTop: '6px' }} key={`${e.code}|${e.path}`}>
            {`${e.message} `}
            <span className="mono muted">{`[${e.code}]`}</span>
          </div>
        ))}
      </div>
      {preset?.hint ? <div className="help">{preset.hint}</div> : null}
      <div className="help">
        Serbest düzenli ifade (regex) alınmaz; yalnız yukarıdaki hazır biçim kuralları kullanılabilir. Bozuk
        bir sınır ya da geçersiz varsayılan değer, yayın kontrol listesini de bloklar.
      </div>
    </details>
  );
}

function FieldsTab({ ctx }: { ctx: PanelContext }): ReactElement {
  const { layout: lay } = ctx;
  const toast = useToast();
  const addFieldSel = useRef<HTMLSelectElement>(null);
  const nfLabel = useRef<HTMLInputElement>(null);
  const nfKey = useRef<HTMLInputElement>(null);
  const nfType = useRef<HTMLSelectElement>(null);

  const flds = lay.fieldDefinitions || [];
  const used = new Set(flds.map((f) => f.key));
  const addable = FIELD_CATALOG.filter((f) => !used.has(f.key));

  /** The `[data-ff]` listener — `m08-admin.js:522`: no undo step, and `sample` also feeds the stage. */
  const setFieldProp = (key: string, prop: string, value: string | boolean): void =>
    ctx.edit((working) => {
      const f = (working.fieldDefinitions || []).find((x) => x.key === key);
      if (!f) return;
      (f as unknown as Record<string, unknown>)[prop] = value;
      if (prop === 'sample' && typeof value === 'string') ctx.setTestValue(f.key, value);
    });

  /** The `[data-vr]` listener — `m08-admin.js:530`; an emptied rule is deleted, not stored blank. */
  const setRule = (key: string, prop: string, raw: string): void =>
    ctx.edit((working) => {
      const f = (working.fieldDefinitions || []).find((x) => x.key === key);
      if (!f) return;
      const target = f as unknown as Record<string, unknown>;
      if (prop === 'defaultValue') {
        if (raw === '') delete target.defaultValue;
        else target.defaultValue = raw;
        return;
      }
      const V = (target.validation ?? {}) as Record<string, unknown>;
      target.validation = V;
      const numeric = ['minLength', 'maxLength', 'min', 'max'].includes(prop);
      if (raw === '' || (prop === 'patternPreset' && raw === 'none')) delete V[prop];
      else V[prop] = numeric ? Number(raw) : raw;
      if (!Object.keys(V).length) delete target.validation;
    });

  const deleteField = (k: string): void => {
    /* Both refusals are the reference's (`m08-admin.js:551`). They are evaluated before the
       commit, so a refused delete does not leave a no-op step on the undo stack. */
    if (lay.layers.some((l) => l.fieldKey === k)) {
      toast('Bu alana bağlı katman var; önce katmanı silin veya başka alana bağlayın');
      return;
    }
    if (k === holderFieldKey(asRenderLayout(lay))) {
      toast('Sahip alanı silinemez');
      return;
    }
    ctx.commit((working) => {
      working.fieldDefinitions = (working.fieldDefinitions || []).filter((f) => f.key !== k);
    });
  };

  const addCatalogField = (): void =>
    ctx.commit((working) => {
      const k = addFieldSel.current?.value;
      if (!k) return;
      const cat = catalogField(k);
      working.fieldDefinitions.push(makeField(k, { importable: !cat?.auto }));
      ctx.setTestValue(k, cat?.sample ?? '');
    });

  const addCustomField = (): void => {
    const label = (nfLabel.current?.value ?? '').trim();
    let key = (nfKey.current?.value ?? '').trim() || slugify(label).replace(/\./g, '_');
    key = key.replace(/[^a-z0-9_]/gi, '_').toLowerCase();
    if (!label || !key) {
      toast('Ad ve anahtar gerekli');
      return;
    }
    if ((lay.fieldDefinitions || []).some((f) => f.key === key)) {
      toast('Bu anahtar zaten var');
      return;
    }
    const type = nfType.current?.value ?? 'string';
    ctx.commit((working) => {
      working.fieldDefinitions.push(
        makeField(
          { key, tr: label, en: label, dataType: type, sample: label },
          {
            importable: !isGraphicDataType(type),
            /* An e-mail field is re-forced to private by `makeField` itself; everything else a
               custom field can be starts on the reference's own metadata + render default. */
            visibility: type === 'email' ? 'private' : 'public_metadata_and_render',
            custom: true,
          },
        ),
      );
      ctx.setTestValue(key, label);
    });
    toast(`«${label}» alanı eklendi`);
  };

  return (
    <>
      <div className="section">
        <h4>
          {'Alan tanımları '}
          <span className="small muted">{`(${flds.length})`}</span>
        </h4>
        <div className="stack" style={{ gap: '6px' }}>
          {flds.map((f) => (
            <div className="fld-item" data-fld={f.key} key={f.key}>
              <div className="row">
                <b>{fieldLabel(f, 'tr')}</b>
                <span className="small muted mono">{f.key}</span>
              </div>
              <div className="row small">
                <span className="pill pill-neutral none">{dataTypeRec(f.dataType).tr}</span>
                {f.auto ? <span className="pill pill-info none">otomatik</span> : null}
                {f.custom ? <span className="pill pill-info none">özel alan</span> : null}
                {isSystemPrivate(f.key) ? (
                  <span className="pill pill-neutral none">sistem · daima gizli</span>
                ) : null}
                {boundToRender(lay, f.key) ? (
                  <span className="pill pill-info none">görselde kullanılıyor</span>
                ) : null}
              </div>
              <div className="row">
                <label className="check">
                  <input
                    type="checkbox"
                    data-ff="required"
                    data-k={f.key}
                    checked={!!f.required}
                    onChange={(e) => setFieldProp(f.key, 'required', e.target.checked)}
                  />
                  {' Zorunlu'}
                </label>
                <label className="check">
                  <input
                    type="checkbox"
                    data-ff="importable"
                    data-k={f.key}
                    checked={!!f.importable}
                    disabled={f.auto && f.key !== 'certification_period'}
                    onChange={(e) => setFieldProp(f.key, 'importable', e.target.checked)}
                  />
                  {" Excel'den gelir"}
                </label>
                {flds.length > 1 ? (
                  <button
                    className="icon-btn xs"
                    type="button"
                    data-delf={f.key}
                    title="Alanı sil"
                    onClick={() => deleteField(f.key)}
                  >
                    ✕
                  </button>
                ) : null}
              </div>
              <div className="row">
                <label className="field inline" style={{ margin: '0', flex: '1' }}>
                  <span className="small muted">Görünürlük</span>
                  <select
                    className="select select-sm"
                    data-ff="visibility"
                    data-k={f.key}
                    disabled={isSystemPrivate(f.key)}
                    aria-label={`${fieldLabel(f, 'tr')} görünürlüğü`}
                    value={f.visibility}
                    onChange={(e) => setFieldProp(f.key, 'visibility', e.target.value)}
                  >
                    {VISIBILITY.map((v) => (
                      <option value={v} key={v}>
                        {VISIBILITY_TR[v]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field inline" style={{ margin: '0', flex: '1' }}>
                  <span className="small muted">Veri türü</span>
                  <select
                    className="select select-sm"
                    data-ff="dataType"
                    data-k={f.key}
                    disabled={f.auto}
                    aria-label={`${fieldLabel(f, 'tr')} veri türü`}
                    value={f.dataType}
                    onChange={(e) => setFieldProp(f.key, 'dataType', e.target.value)}
                  >
                    {FIELD_DATA_TYPES.map((x) => (
                      <option value={x.key} key={x.key}>
                        {x.tr}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="row">
                {/* `data-ff="labelTr"` writes `f.labelTr`, not `f.labels.tr` (`m08-admin.js:522`),
                    so the reference's own label list does not follow what is typed here. Both
                    inputs are therefore uncontrolled: the typed text stays visible exactly as it
                    does upstream, and the model receives exactly what upstream writes. */}
                <input
                  className="input input-sm"
                  data-ff="labelTr"
                  data-k={f.key}
                  defaultValue={fieldLabel(f, 'tr')}
                  aria-label="TR etiket"
                  onChange={(e) => setFieldProp(f.key, 'labelTr', e.target.value)}
                />
                <input
                  className="input input-sm"
                  data-ff="labelEn"
                  data-k={f.key}
                  defaultValue={fieldLabel(f, 'en')}
                  aria-label="EN etiket"
                  onChange={(e) => setFieldProp(f.key, 'labelEn', e.target.value)}
                />
                <SyncedInput
                  className="input input-sm"
                  data-ff="sample"
                  data-k={f.key}
                  value={f.sample || ''}
                  placeholder="örnek değer"
                  aria-label="Örnek"
                  onValue={(v) => setFieldProp(f.key, 'sample', v)}
                />
              </div>
              {f.auto ? null : <RuleEditor field={f} onRule={(prop, raw) => setRule(f.key, prop, raw)} />}
              {boundToRender(lay, f.key) && !canRender(f.visibility) ? (
                <div className="warn-box small">
                  Bu alan görsel katmana bağlı ama görünürlüğü render'a izin vermiyor — yayın reddedilir.
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <div className="section">
        <h4>Alan ekle</h4>
        {addable.length ? (
          <div style={{ display: 'flex', gap: '6px' }}>
            <select className="select select-sm" id="addFieldSel" style={{ flex: '1' }} ref={addFieldSel}>
              {addable.map((f) => (
                <option value={f.key} key={f.key}>
                  {f.tr}
                </option>
              ))}
            </select>
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              id="addFieldBtn"
              onClick={addCatalogField}
            >
              Ekle
            </button>
          </div>
        ) : (
          <p className="muted small">Katalogdaki tüm alanlar eklendi.</p>
        )}
        <div className="divider" style={{ margin: '10px 0' }} />
        <div className="field">
          <label htmlFor="nf_label">Özel alan adı</label>
          <input className="input input-sm" id="nf_label" placeholder="ör. Şube Kodu" ref={nfLabel} />
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="nf_key">Anahtar</label>
            <input className="input input-sm" id="nf_key" placeholder="sube_kodu" ref={nfKey} />
          </div>
          <div className="field">
            <label htmlFor="nf_type">Tür</label>
            <select className="select select-sm" id="nf_type" ref={nfType}>
              {FIELD_DATA_TYPES.filter(
                (x) => x.key !== 'credential_id' && x.key !== 'credential_url' && x.key !== 'qr',
              ).map((x) => (
                <option value={x.key} key={x.key}>
                  {x.tr}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button
          className="btn btn-secondary btn-sm"
          type="button"
          id="addCustomField"
          style={{ justifySelf: 'start' }}
          onClick={addCustomField}
        >
          Özel alan oluştur
        </button>
        <div className="help">
          Özel alan eklemek için kod değişikliği gerekmez: alan, editörde katmana bağlanabilir ve toplu üretim
          kolon eşlemesinde otomatik görünür.
        </div>
      </div>
    </>
  );
}

// ------------------------------------------------------------------------------- ŞABLON sekmesi

/**
 * v0.5: this tab edits the DRAFT (`ED.layout`). The root template carries only the name and the
 * short code; meaning, policy and permissions are frozen into a release. A saved draft change
 * touches no credential until the next release is published (rules 3 and 4).
 */
function TemplateTab({ ctx }: { ctx: PanelContext }): ReactElement {
  const { layout: lay, template: t } = ctx;
  const db = useDemoData();
  const commands = useV07Commands();
  /** The management name as the server last confirmed it, per template. */
  const persisted = useRef({ templateId: t.id, name: t.name });
  /**
   * The newest committed name waiting to be sent, and the one on its way.
   *
   * Refs, not state: the command layer refreshes the whole store from the server when a write
   * succeeds, and a value that lived in the store would be overwritten by that refresh before it
   * was ever sent. A ref survives it, which is the point.
   */
  const pendingName = useRef<string | null>(null);
  const flightName = useRef<string | null>(null);
  /** What is in the box right now, committed or not — an edit nobody has finished is work too. */
  const typedName = useRef<string | null>(null);
  const savingName = useRef(false);
  /** False once this panel is gone; a late answer may not write into a tree that is not there. */
  const alive = useRef(true);
  const [nameState, setNameState] = useState<{ tone: 'busy' | 'ok' | 'bad'; text: string } | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const hasCreds = tplCount(db, t.id) > 0;
  const md = lay.localizedMetadata;
  const perm = lay.permissions;
  const orgLock = lay.subjectType === 'organization';
  const rels = t.releases || [];
  const liIssuer = lay.linkedInIssuer ?? null;
  /*
   * The same rule the server will apply, run here only so the message lands on the field that is
   * wrong instead of arriving as one refusal after «Taslağı kaydet». The server still validates:
   * this is a hint, not the gate.
   */
  const liIssuerErrors = linkedInIssuerFieldErrors(liIssuer);
  /*
   * The same reading the publish gate does over the sealed languages, run here for the same reason:
   * so «English sürümü için başlık yok» lands on the English box instead of arriving as one refusal
   * after «Yayınla». The server is still the gate.
   */
  const sealed = sealedLanguagesOf(lay.sealedLanguages);
  const localeGaps = sealedLocaleGaps({
    defaultLanguage: lay.defaultLanguage,
    sealedLanguages: lay.sealedLanguages,
    localizedMetadata: md as unknown as Record<string, Record<string, unknown>>,
    layers: lay.layers,
    background: lay.background,
    localizedBackground: lay.localizedBackground,
  });
  /* Two different facts, kept apart: what the publish gate refuses, and what the public page would
     have no name to show. A template that never draws the name on the artwork can be sealed without
     a translated title and STILL leave the English page nameless. */
  const nameGaps = publicNameGaps({
    defaultLanguage: lay.defaultLanguage,
    sealedLanguages: lay.sealedLanguages,
    localizedMetadata: md as unknown as Record<string, Record<string, unknown>>,
    layers: lay.layers,
    background: lay.background,
    localizedBackground: lay.localizedBackground,
  });
  const titleGap = (locale: 'tr' | 'en'): boolean =>
    localeGaps.some((g) => g.locale === locale && g.where === 'title') || nameGaps.includes(locale);
  /** «Seal the other language too» — the only choice there is, with two locales. */
  const other = otherLocale(lay.defaultLanguage);
  const sealsBoth = sealed.length > 1;
  const setSealsBoth = (on: boolean): void =>
    ctx.commit((working) => {
      /* The declared language is always sealed; the checkbox only adds or removes the other one.
         Written in the product's own order so the stored value is stable. */
      working.sealedLanguages = on
        ? (['tr', 'en'] as const).filter((l) => l === working.defaultLanguage || l === other)
        : [];
    });

  /**
   * One of the two boxes changed.
   *
   * The raw text is kept as typed — the server normalises it to the canonical company address and
   * the editor re-reads what was stored — and both boxes empty removes the setting outright
   * rather than storing an empty organisation.
   */
  const setLinkedInIssuer = (key: 'organizationName' | 'pageUrl', value: string): void =>
    ctx.edit((working) => {
      const current = working.linkedInIssuer ?? { organizationName: '', pageUrl: null };
      const next =
        key === 'organizationName'
          ? { organizationName: value, pageUrl: current.pageUrl }
          : { organizationName: current.organizationName, pageUrl: value };
      working.linkedInIssuer =
        next.organizationName.trim() === '' && (next.pageUrl ?? '').trim() === '' ? null : next;
    });

  /* `touchRoot` — `m08-admin.js:608`. Name and short code are written to the template RECORD and
     saved with it: they are not part of the draft and do not make it dirty. */
  const touchRoot = (key: 'name' | 'code', value: string): void =>
    commands.patchLocal((store) => {
      const rec = store.templates.find((x) => x.id === t.id);
      if (!rec) return;
      rec[key] = value;
      rec.updatedAt = prototypeNowUTC();
      rec.userModified = true;
    });

  /**
   * The management name, WRITTEN ON THE SERVER.
   *
   * `touchRoot` keeps the box responsive by patching the browser's own store, and for years that
   * was all that happened: leave the editor, reload, come back, and the old name was there again,
   * because no route could change `templates.name`. The same command the card's «Adını değiştir»
   * uses is called here, on commit, so there is ONE path and not two.
   *
   * WHAT A SLOW ANSWER MAY NOT DO
   * -----------------------------
   * Three things used to be decided by whichever answer arrived last rather than by what the
   * person had typed: a success wrote the name it had SENT over a newer one, a refusal wrote the
   * name the SERVER still held over a newer one, and two commits in flight at once let the older
   * one land second. So there is one request at a time per template, the newest committed name
   * waits in a ref while it runs, an answer only ever describes the request it belongs to, and the
   * server is told which name this request believed was stored so it can refuse an overtaken one
   * (`expectedName`) instead of applying it.
   *
   * A refusal keeps what was typed and says why; it does not put the old name back, because the
   * person has not changed their mind — the write failed.
   */
  const pumpName = async (templateId: string): Promise<void> => {
    if (savingName.current) return;
    savingName.current = true;
    try {
      while (pendingName.current !== null) {
        if (persisted.current.templateId !== templateId) return;
        const want = pendingName.current;
        pendingName.current = null;
        if (want === persisted.current.name) {
          /* Already what the server holds — typed back, or confirmed by the request before. */
          if (alive.current) setNameState({ tone: 'ok', text: `Kaydedildi: ${want}` });
          continue;
        }
        flightName.current = want;
        if (alive.current) setNameState({ tone: 'busy', text: 'Kaydediliyor…' });
        const r = await commands.renameTemplate({
          templateId,
          name: want,
          expectedName: persisted.current.name,
        });
        flightName.current = null;
        /* Another template, or no panel at all: this answer has nothing left to describe. */
        if (!alive.current || persisted.current.templateId !== templateId) return;
        if (r.ok) persisted.current = { templateId, name: r.message ?? want };
        /*
         * The store may have been replaced by the refresh the command layer runs on success, so
         * whatever should be on screen is written back here: a newer name if one was typed while
         * this was open, the name the server stored if the write succeeded, and the name that was
         * refused if it did not.
         */
        const inBox = typedName.current;
        const newerTyped = inBox === null || inBox.trim() === want ? null : inBox;
        const newer = pendingName.current ?? newerTyped;
        touchRoot('name', newer ?? (r.ok ? persisted.current.name : want));
        setNameState(
          r.ok
            ? { tone: 'ok', text: `Kaydedildi: ${persisted.current.name}` }
            : { tone: 'bad', text: r.message ?? 'Şablon adı değiştirilemedi' },
        );
        /* A refusal is not retried on its own; a NEWER name the person typed still is. */
        if (!r.ok && newer === null) return;
      }
    } finally {
      savingName.current = false;
    }
  };

  /** Blur or Enter on the «Ad» box. */
  const saveName = (value: string): void => {
    const name = value.trim();
    if (persisted.current.templateId !== t.id) persisted.current = { templateId: t.id, name: t.name };
    if (name === '') {
      /*
       * Refused where it was typed. The old name is NOT written back over it: silently undoing
       * what somebody typed is how an edit disappears without anybody being told.
       */
      setNameState({ tone: 'bad', text: 'Şablon adı boş olamaz.' });
      return;
    }
    /* Enter, and the blur it causes, are one commit. */
    if (name === pendingName.current || name === flightName.current) return;
    if (name === persisted.current.name && !savingName.current) {
      setNameState(null);
      return;
    }
    pendingName.current = name;
    void pumpName(t.id);
  };

  const setMeta = (lang: string, key: string, value: string): void =>
    ctx.edit((working) => {
      const bag = (working.localizedMetadata[lang] ?? {}) as Record<string, unknown>;
      working.localizedMetadata[lang] = bag as (typeof working.localizedMetadata)[string];
      bag[key] = value;
    });

  /** The `[data-d]` listener — `m08-admin.js:609`, including the subject-type cascade. */
  const setDraftField = (key: string, value: string): void => {
    ctx.commit((working) => {
      (working as unknown as Record<string, unknown>)[key] = value;
      if (key !== 'subjectType') return;
      working.holderField = value === 'organization' ? 'organization_name' : 'recipient_name';
      /* Rule 13 — an organization credential has no personal LinkedIn profile-add flow. */
      if (value === 'organization') working.permissions.allowLinkedInProfile = false;
      const want = working.holderField;
      if (!(working.fieldDefinitions || []).some((f) => f.key === want))
        working.fieldDefinitions.unshift(makeField(want, { required: true, importable: true }));
    });
    if (key !== 'subjectType') return;
    /* `ED.testData = defaultTestData(t, lay)` — read AFTER the cascade, so the samples come from
       the new holder field. The layout is mutated in place, so this is that layout. */
    for (const [k, v] of Object.entries(defaultTestData(t, lay))) ctx.setTestValue(k, v);
  };

  return (
    <>
      <div className="section">
        <h4>Şablon kimliği</h4>
        <div className="field-row">
          <div className="field">
            <label htmlFor="tp_name">Ad</label>
            <SyncedInput
              className="input input-sm"
              id="tp_name"
              data-root="name"
              value={t.name}
              aria-describedby={nameState === null ? undefined : 'tp_nameState'}
              onValue={(v) => {
                typedName.current = v;
                touchRoot('name', v);
                if (nameState !== null) setNameState(null);
              }}
              onCommit={(v) => saveName(v)}
            />
            {nameState === null ? null : (
              <div
                className="help"
                id="tp_nameState"
                data-tpl-name-state={nameState.tone}
                {...(nameState.tone === 'bad' ? { 'data-tpl-name-error': '' } : {})}
                role={nameState.tone === 'bad' ? 'alert' : 'status'}
                {...(nameState.tone === 'bad' ? { style: { color: 'var(--red)' } } : {})}
              >
                {nameState.text}
              </div>
            )}
          </div>
          <div className="field">
            <label htmlFor="tp_code">Kısa kod</label>
            <SyncedInput
              className="input input-sm"
              id="tp_code"
              data-root="code"
              value={t.code}
              onValue={(v) => touchRoot('code', v)}
            />
          </div>
        </div>
        <div className="help">
          Ad ve kısa kod yalnız yönetim ekranında kullanılır; public sayfa ve credential anlamı release
          snapshot'ından gelir.
        </div>
      </div>

      <div className="section">
        <h4>Release içeriği (taslak)</h4>
        <div className="field">
          <label htmlFor="tp_title">Sertifika adı (LinkedIn / public sayfa)</label>
          <SyncedInput
            className="input input-sm"
            id="tp_title"
            data-md="tr.title"
            value={md.tr?.title || ''}
            onValue={(v) => setMeta('tr', 'title', v)}
          />
          {/* Which box the head's language selection is pointing at, said on the box itself. */}
          {ctx.editLocale === 'tr' ? (
            <div className="help" data-hpw-working-locale="tr">
              Düzenlenen dil: Türkçe. Bu ad Türkçe belgeye ve Türkçe public sayfaya çizilir.
            </div>
          ) : null}
          {titleGap('tr') ? (
            <div className="warn-box small" id="tp_titleGapTr">
              {localeGaps.some((g) => g.locale === 'tr' && g.where === 'title')
                ? 'Türkçe sürüm mühürleniyor ve bu ad belgeye çiziliyor, ama Türkçe başlık yok. Bu alan boşken yayın reddedilir; diğer dilin başlığı Türkçe belgeye çizilmez.'
                : 'Türkçe sürüm mühürleniyor ama Türkçe başlık yok. Belgeye çizilmediği için yayın engellenmez; Türkçe public sayfada kaydın adı boş kalır.'}
            </div>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="tp_titleEn">Credential name (EN)</label>
          <SyncedInput
            className="input input-sm"
            id="tp_titleEn"
            data-md="en.title"
            value={md.en?.title || ''}
            onValue={(v) => setMeta('en', 'title', v)}
          />
          {ctx.editLocale === 'en' ? (
            <div className="help" data-hpw-working-locale="en">
              Düzenlenen dil: English. Bu ad İngilizce belgeye ve İngilizce public sayfaya çizilir.
            </div>
          ) : null}
          {titleGap('en') ? (
            <div className="warn-box small" id="tp_titleGapEn">
              {localeGaps.some((g) => g.locale === 'en' && g.where === 'title')
                ? 'İngilizce sürüm mühürleniyor ve bu ad belgeye çiziliyor, ama İngilizce başlık yok. Bu alan boşken yayın reddedilir; İngilizce sayfada Türkçe başlık gösterilmez.'
                : 'İngilizce sürüm mühürleniyor ama İngilizce başlık yok. Belgeye çizilmediği için yayın engellenmez; İngilizce public sayfada kaydın adı boş kalır ve Türkçe adı gösterilmez.'}
            </div>
          ) : null}
        </div>
        {/*
         * WHICH LANGUAGES THIS TEMPLATE SEALS A DOCUMENT IN.
         *
         * Off is today's behaviour and the behaviour of every template built before this existed:
         * one document, in the declared language. On means every issuance seals two — and then the
         * publish gate requires the words and the artwork for both, and says which is missing. It is
         * deliberately a decision the author makes rather than something inferred from a filled-in
         * English box: an English title is not a promise that the artwork has been translated.
         */}
        <div className="field">
          <label className="check" htmlFor="tp_sealBoth">
            <input
              type="checkbox"
              id="tp_sealBoth"
              data-testid="editor-seal-both"
              checked={sealsBoth}
              onChange={(e) => setSealsBoth(e.target.checked)}
            />
            {' Her iki dilde belge mühürle (Türkçe + English)'}
          </label>
          <div className="help">
            {sealsBoth
              ? 'Her sertifika iki belge olarak mühürlenir: aynı kayıt, aynı kod, aynı adres. Public sayfadaki dil seçimi hangi belgenin gösterileceğini belirler.'
              : `Tek dilde mühürlenir: ${localeLabel(lay.defaultLanguage)}. Bu, bugünkü davranıştır.`}
          </div>
          {localeGaps.length > 0 ? (
            <div className="warn-box small" id="tp_localeGaps">
              <b>{`İkinci dil için eksik olanlar (${localeGaps.length}):`}</b>
              <ul>
                {localeGaps.map((g) => (
                  <li key={`${g.locale}:${g.where}`}>
                    {g.where === 'defaultLanguage'
                      ? 'Şablon hangi dilde yazıldığını bildirmiyor; iki dil mühürleyen şablon bunu bildirmek zorundadır.'
                      : g.where === 'title'
                        ? `${localeLabel(g.locale)} başlık yok${g.wouldDraw ? ` — «${g.wouldDraw}» çizilirdi` : ''}.`
                        : g.where === 'background'
                          ? `${localeLabel(g.locale)} sertifika görseli seçilmedi — ${g.wouldDraw} çizilirdi.`
                          : `${localeLabel(g.locale)} metni yok: ${g.where.slice('layer:'.length)} — «${g.wouldDraw}» çizilirdi.`}
                  </li>
                ))}
              </ul>
              {'Bunlar doldurulmadan yayın reddedilir ('}
              <span className="mono">secondary_locale_render_not_configured</span>
              {'). Sunucu yine de son karar mercidir.'}
            </div>
          ) : null}
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="tp_type">Belge türü</label>
            <select
              className="select select-sm"
              id="tp_type"
              data-d="credentialType"
              disabled={hasCreds}
              value={lay.credentialType}
              onChange={(e) => setDraftField('credentialType', e.target.value)}
            >
              {CREDENTIAL_TYPES.map((x) => (
                <option value={x.key} key={x.key}>
                  {x.tr}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="tp_holder">Alıcı türü</label>
            <select
              className="select select-sm"
              id="tp_holder"
              data-d="subjectType"
              disabled={hasCreds}
              value={lay.subjectType}
              onChange={(e) => setDraftField('subjectType', e.target.value)}
            >
              {SUBJECT_TYPES.map((x) => (
                <option value={x.key} key={x.key}>
                  {x.tr}
                </option>
              ))}
            </select>
          </div>
        </div>
        {lay.credentialType === 'custom' ? (
          <div className="field">
            <label htmlFor="tp_custom">Özel tür adı (görünen)</label>
            <CommitInput
              className="input input-sm"
              id="tp_custom"
              data-d="customTypeLabel"
              value={lay.customTypeLabel || ''}
              placeholder="ör. onur beratı"
              onCommit={(v) => setDraftField('customTypeLabel', v)}
            />
          </div>
        ) : null}
        <div className="field-row">
          <div className="field">
            <label htmlFor="tp_validity">Geçerlilik (ay)</label>
            <CommitInput
              className="input input-sm"
              id="tp_validity"
              data-validity="1"
              type="number"
              min="0"
              value={
                lay.validityPolicy && lay.validityPolicy.months != null
                  ? String(lay.validityPolicy.months)
                  : ''
              }
              placeholder="boş = süresiz"
              onCommit={(v) =>
                ctx.edit((working) => {
                  const n = v === '' ? null : Number.parseInt(v, 10);
                  working.validityPolicy = { mode: n ? 'months' : 'none', months: n };
                })
              }
            />
          </div>
          <div className="field">
            <label htmlFor="tp_lang">Varsayılan dil</label>
            <select
              className="select select-sm"
              id="tp_lang"
              data-d="defaultLanguage"
              value={lay.defaultLanguage}
              onChange={(e) => setDraftField('defaultLanguage', e.target.value)}
            >
              <option value="tr">Türkçe</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="tp_holderfield">Sahip alanı (subject kimliği)</label>
          <select
            className="select select-sm"
            id="tp_holderfield"
            data-d="holderField"
            value={holderFieldKey(asRenderLayout(lay))}
            onChange={(e) => setDraftField('holderField', e.target.value)}
          >
            {(lay.fieldDefinitions || [])
              .filter((f) => !f.auto && !isSystemPrivate(f.key))
              .map((f) => (
                <option value={f.key} key={f.key}>
                  {fieldLabel(f, 'tr')}
                </option>
              ))}
          </select>
        </div>
        {hasCreds ? (
          <div className="help">
            Kayıt üretilmiş şablonun türü ve alıcı türü değiştirilemez; farklı tür için «Kopyasını Oluştur»
            kullanın.
          </div>
        ) : null}
      </div>

      <div className="section">
        <h4>Yayın politikası</h4>
        <div className="stack" style={{ gap: '6px' }}>
          {PUBLICATION_POLICIES.map((p) => (
            <label className="radio" key={p.key}>
              <input
                type="radio"
                name="tp_pol"
                value={p.key}
                checked={lay.publicationPolicy === p.key}
                onChange={() =>
                  ctx.edit((working) => {
                    working.publicationPolicy = p.key;
                  })
                }
              />
              <span>
                <b>{p.tr}</b>
                <span>{p.help}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="help">
          {'Tek enum: '}
          <code>publicationPolicy</code>
          {
            ". Ayrı «public varsayılan» ve «alıcı onayı» boolean'ları kaldırıldı; durum makinesi ve UI bu enumdan üretilir."
          }
        </div>
      </div>

      <div className="section">
        <h4>Public metin</h4>
        <div className="field">
          <label htmlFor="tp_descTr">Açıklama (TR)</label>
          <SyncedTextarea
            className="textarea"
            id="tp_descTr"
            data-md="tr.description"
            style={{ minHeight: '64px' }}
            value={md.tr?.description || ''}
            onValue={(v) => setMeta('tr', 'description', v)}
          />
        </div>
        <div className="field">
          <label htmlFor="tp_descEn">Açıklama (EN)</label>
          <SyncedTextarea
            className="textarea"
            id="tp_descEn"
            data-md="en.description"
            style={{ minHeight: '64px' }}
            value={md.en?.description || ''}
            onValue={(v) => setMeta('en', 'description', v)}
          />
        </div>
        <div className="field">
          <label htmlFor="tp_critTr">Verilme kriteri (TR)</label>
          <SyncedTextarea
            className="textarea"
            id="tp_critTr"
            data-md="tr.criteria"
            style={{ minHeight: '56px' }}
            value={md.tr?.criteria || ''}
            onValue={(v) => setMeta('tr', 'criteria', v)}
          />
        </div>
        <div className="field">
          <label htmlFor="tp_critEn">Verilme kriteri (EN)</label>
          <SyncedTextarea
            className="textarea"
            id="tp_critEn"
            data-md="en.criteria"
            style={{ minHeight: '56px' }}
            value={md.en?.criteria || ''}
            onValue={(v) => setMeta('en', 'criteria', v)}
          />
        </div>
      </div>

      <div className="section">
        <h4>İzinler</h4>
        <label className="check">
          <input
            type="checkbox"
            data-perm="allowLinkedInProfile"
            checked={perm.allowLinkedInProfile}
            disabled={orgLock}
            onChange={(e) =>
              ctx.edit((working) => {
                working.permissions.allowLinkedInProfile = e.target.checked;
              })
            }
          />
          {orgLock ? ' LinkedIn profiline ekleme ' : ' LinkedIn profiline ekleme'}
          {orgLock ? (
            <span className="small muted">{"— kuruluş credential'ında domain düzeyinde kapalı"}</span>
          ) : null}
        </label>
        <label className="check">
          <input
            type="checkbox"
            data-perm="allowLinkedInShare"
            checked={perm.allowLinkedInShare}
            onChange={(e) =>
              ctx.edit((working) => {
                working.permissions.allowLinkedInShare = e.target.checked;
              })
            }
          />
          {" LinkedIn'de gönderi olarak paylaşma"}
        </label>
        <label className="check">
          <input
            type="checkbox"
            data-perm="allowDownload"
            checked={perm.allowDownload}
            onChange={(e) =>
              ctx.edit((working) => {
                working.permissions.allowDownload = e.target.checked;
              })
            }
          />
          {' PNG / yazdırma indirme'}
        </label>
      </div>

      {/*
       * The badge or certificate's own «Veren kuruluş» for LinkedIn — one optional line.
       *
       * It changes the issuing organisation the LinkedIn profile form is opened with, and
       * NOTHING else: not the issuer of record, not the «Veren kurum» row of the verification
       * page, not the drawing, not the e-mail sender, not the account a post is published from.
       * Left empty, the deployment's own organisation is used exactly as before.
       *
       * Stored on this template's draft and frozen into each release with the rest of the model,
       * so editing it tomorrow leaves documents issued today alone (rules 3 and 4).
       */}
      <div className="section" id="tp_liIssuerSection">
        <h4>{"LinkedIn'de veren kuruluş — isteğe bağlı"}</h4>
        <div className="field-row">
          <div className="field">
            <label htmlFor="tp_liOrgName">Kuruluş adı</label>
            <SyncedInput
              className="input input-sm"
              id="tp_liOrgName"
              data-li-issuer="organizationName"
              aria-describedby={liIssuerErrors.organizationName ? 'tp_liOrgNameErr' : undefined}
              value={liIssuer?.organizationName ?? ''}
              onValue={(v) => setLinkedInIssuer('organizationName', v)}
            />
            {liIssuerErrors.organizationName ? (
              <div className="help" id="tp_liOrgNameErr" role="alert" style={{ color: 'var(--red)' }}>
                {liIssuerErrors.organizationName}
              </div>
            ) : null}
          </div>
          <div className="field">
            <label htmlFor="tp_liOrgPage">Kuruluşun LinkedIn sayfası</label>
            <SyncedInput
              className="input input-sm"
              id="tp_liOrgPage"
              data-li-issuer="pageUrl"
              aria-describedby={liIssuerErrors.pageUrl ? 'tp_liOrgPageErr' : undefined}
              value={liIssuer?.pageUrl ?? ''}
              onValue={(v) => setLinkedInIssuer('pageUrl', v)}
            />
            {liIssuerErrors.pageUrl ? (
              <div className="help" id="tp_liOrgPageErr" role="alert" style={{ color: 'var(--red)' }}>
                {liIssuerErrors.pageUrl}
              </div>
            ) : null}
          </div>
        </div>
        <div className="help">
          Yalnız LinkedIn profilindeki veren kuruluş bilgisini değiştirir. Boş bırakılırsa mevcut kuruluş
          bilgisi kullanılır.
        </div>
      </div>

      <div className="section">
        <h4>Release geçmişi</h4>
        {rels.length ? (
          <div className="stack" style={{ gap: '4px' }}>
            {rels
              .slice()
              .reverse()
              .map((r) => (
                <div className="layer-item" key={r.id}>
                  <span>{`v${r.releaseNumber}${r.id === t.currentReleaseId ? ' · güncel' : ''}`}</span>
                  <span className="small muted mono" style={{ marginLeft: 'auto' }}>
                    {`${String(r.releaseHash).slice(0, 12)}… · ${fmtDateTime(r.releasedAt)} · ${canvasW(asCanvas(r.canvas))}×${canvasH(asCanvas(r.canvas))}`}
                  </span>
                </div>
              ))}
          </div>
        ) : (
          <p className="muted small">Henüz yayınlanmış release yok.</p>
        )}
        <div className="help">
          {'Yayınlanan release recursive olarak dondurulur ve '}
          <code>releaseHash</code>
          {
            " ile içeriğine bağlanır. Taslakta yapılan değişiklikler yeni release yayınlanana kadar hiçbir credential'ı etkilemez."
          }
        </div>
      </div>
    </>
  );
}

// -------------------------------------------------------------------------------------- the panel

function PropsPanelView(props: PropsPanelProps): ReactElement {
  const { template, layout, tab } = props;
  const fonts = props.fonts ?? seedFonts();
  const ctx: PanelContext = {
    template,
    layout,
    sel: props.sel,
    aspectLock: props.aspectLock,
    assets: props.assets ?? seedAssets(),
    fonts,
    commit: props.onMutate,
    edit: props.onEdit ?? props.onMutate,
    select: props.onSelect,
    setTestValue: props.onTestData,
    showSafeAreas: props.onShowSafeAreas ?? ((): void => undefined),
    setAspectLock: props.onAspectLock,
    fontFile: props.onFontFile ?? ((): void => undefined),
    library: props.library,
    editLocale: props.editLocale ?? 'tr',
  };
  const structureKey =
    props.propsKey ?? structureKeyOf(template, layout, tab, props.sel, familyList(fonts), ctx.editLocale);

  return (
    <>
      <div className="card-head">
        <h2>Özellikler</h2>
        <TemplatePill template={template} />
      </div>
      <div className="prop-tabs">
        {EDITOR_TABS.map(([k, lb]) => (
          <button
            type="button"
            className={tab === k ? 'is-on' : ''}
            data-ptab={k}
            key={k}
            onClick={() => props.onTab(k)}
          >
            {lb}
          </button>
        ))}
      </div>
      {/* `ED.propsKey` — the body is rebuilt when the STRUCTURE changes and only then; a value
          change re-renders it in place, which is what keeps the caret. See the file header. */}
      <div id="propBody" key={structureKey}>
        {tab === 'canvas' ? (
          <CanvasTab ctx={ctx} />
        ) : tab === 'fields' ? (
          <FieldsTab ctx={ctx} />
        ) : tab === 'template' ? (
          <TemplateTab ctx={ctx} />
        ) : (
          <LayerTab ctx={ctx} />
        )}
      </div>
    </>
  );
}

export const PropsPanel = memo(PropsPanelView);
